import axios, { InternalAxiosRequestConfig } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import {
    Client, ClientListResponse, ClientFormData,
    Scheme, SchemeFormData, SchemeListResponse,
    Strategy, StrategyFormData, StrategyListResponse, FASource, FirmTypesResponse,
    ClientCategory, ClientGroup, Distributor, ClientClass,
    Company, CompanyFormData, CompanyListResponse,
    CompanyCategory, Sector, AssetClass,
    HoldingsListResponse, HoldingsByClientResponse,
    HoldingsByCompanyResponse,
    OrderAllocation, OrderAllocationListResponse,
    ContractNote, ContractNoteListResponse, ContractNotesByClientResponse,
    ContractNotesByCompanyResponse,
    CustodyHoldings, CustodyHoldingsListResponse, CustodyHoldingsExportResponse, CustodyHoldingsByClientResponse,
    CustodyHoldingsByCompanyResponse,
    TradeSuggestionsResponse,
    SchemeWiseBroker,
    CompanyVolumeStatsData,
    ExecutionAnalyticsRequest,
    BrokerageComparisonRequest,
    PortfolioLiquidityRiskResponse,
    PortfolioLiquidityRiskRequest, FeeComponentModel,
    ClientApproval, ClientApprovalListResponse,
    ExposureRiskRequest,
    ExposureRiskResponse,
    CustodyAccount,
    CustodyHoldingFileListResponse,
    CustodyIngestResponse,
    SectorWeightResponse,
    SectorWeightRequest,
    BrokerWiseBrokerageRequest,
    ContractFileUploadResponse, ContractFile, ContractFileResponse,
    CustodianAllocationStatus,
    GlobalContractNoteFetchResult,
    ClientFeeNote,
    SchemeWiseHoldingsResponse,
    ExecutionMatrixResponse,
    ClientOrderGenerationFailureInfoListResponse,
    FailureReasonCategory,
    Setting,
    ContributionAnalysisResponse,
    XirrPortfolioPerformanceResponse,
    TwrrPortfolioPerformanceResponse,
    BenchmarkPerformanceResponse,
    HoldingsDateRestriction,
    LivePrice,
    UsdInrRate,
    BankStatementListResponse,
    PaginatedResponse,
    ZohoClientSyncResult,
    ZohoSyncTotals,
    ClientSeriesOption,
} from '@/types/client';
import type {
    BlockTrade,
    BlockTradeChatSharePayload,
    BlockTradeChatShareResponse,
    BlockTradeCreatePayload,
    BlockTradeOverridePayload,
    BlockTradePartyType,
    BlockTradeStatus,
    BlockTradeUpdatePayload,
} from '@/types/blockTrade';
import {
    VendorSymbolMapping,
    VendorSymbolMappingFormData,
    VendorSymbolMappingListResponse
} from '@/types/vendorSymbolMapping';
import { FieldConfig, ExtendedClientFormData } from "@/components/client/types";
import type { PortfolioPEApiResponse } from "@/components/portfolio_pe/types";
import type { QuantityUpdate } from "@/components/orders/types";
import type { Role, RoleDetail, Scope } from '@/types/roles';
import type {
    SoaAssignResponse,
    SoaBounceScanSummary,
    SoaDisclaimerMode,
    SoaDisclaimerStatus,
    SoaDocumentListResponse,
    SoaEmailStatus,
    SoaEmailTemplate,
    SoaMissingClientsResponse,
    SoaReportVariant,
    SoaSendJobState,
    SoaSendSummary,
    SoaUploadSummary,
} from '@/types/soa';
import type { SystemUser, SystemUserListResponse, SystemUserFormData } from '@/types/user';
import type {
    OperationsChecklist,
    OperationStep,
    ChecklistListResponse,
} from '@/types/operations';

const DEFAULT_API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const CLOUD_RUN_API_URL = import.meta.env.VITE_CLOUD_RUN_API_URL;

// Both backends share one database and JWT secret, so a session can switch between
// them mid-flight. Requests sent before LaunchDarkly loads use the default.
let apiBaseUrl = DEFAULT_API_BASE_URL;

const apiClient = axios.create({
    baseURL: apiBaseUrl,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Point the API at the oat Cloud Run service or at VITE_API_URL. Driven by the
 * useCloudRunUrl flag (ApiBaseUrlSync); without VITE_CLOUD_RUN_API_URL the default stays.
 */
export const setCloudRunApiEnabled = (enabled: boolean): void => {
    apiBaseUrl = enabled && CLOUD_RUN_API_URL ? CLOUD_RUN_API_URL : DEFAULT_API_BASE_URL;
    apiClient.defaults.baseURL = apiBaseUrl;
};

// Token decoding helper
const getAccessTokenExp = (token: string): number | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        // Base64URL decode
        let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        // Pad with = if necessary
        while (base64.length % 4) {
            base64 += '=';
        }

        const payload = JSON.parse(atob(base64));
        return payload.exp ?? null;
    } catch {
        return null;
    }
};

let refreshPromise: Promise<string | null> | null = null;

const TOKEN_EXPIRY_SKEW_MS = 30_000;

// Fresh = strictly MORE than TOKEN_EXPIRY_SKEW_MS remaining — the mirror of
// the request interceptor's proactive-refresh trigger (strictly LESS than the
// same skew). The strict ranges are disjoint, so a token reported fresh here
// is never one the interceptor would refresh. Exactly at the skew, neither
// applies: the token is not reused here and not yet refreshed by the
// interceptor — the next request, moments later, lands inside the refresh
// window.
const isAccessTokenFresh = (token: string | null): token is string => {
    if (!token) return false;
    const exp = getAccessTokenExp(token);
    return exp !== null && exp * 1000 - Date.now() > TOKEN_EXPIRY_SKEW_MS;
};

const refreshTokenPair = async (): Promise<string | null> => {
    // Another tab may have finished refreshing while we waited for the
    // cross-tab lock — reuse its access token instead of sending the
    // already-rotated (now blacklisted) refresh token.
    const storedAccess = localStorage.getItem('accessToken');
    if (isAccessTokenFresh(storedAccess)) {
        return storedAccess;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    try {
        const response = await axios.post(`${apiBaseUrl}/api/auth/token/refresh/`, {
            refresh: refreshToken,
        });
        const { access, refresh: newRefresh } = response.data;
        localStorage.setItem('accessToken', access);
        if (newRefresh) {
            localStorage.setItem('refreshToken', newRefresh);
        }
        window.dispatchEvent(new Event('auth-change'));
        return access;
    } catch (error) {
        // The stored refresh token changed while our request was in flight.
        // With Web Locks the ordinary two-tabs-refresh race can't reach here
        // (the lock + freshness pre-check above prevent it) — this guards the
        // writers that do NOT take the lock: login/logout in another tab, tabs
        // still running a pre-lock bundle during a deploy, and the no-locks
        // fallback path. Adopt the newer credentials instead of tearing the
        // session down.
        const currentToken = localStorage.getItem('refreshToken');
        if (currentToken && currentToken !== refreshToken) {
            return localStorage.getItem('accessToken');
        }

        // Log out only when the refresh token is definitively rejected
        // (400/401/403). Transient failures (network, timeout, 5xx during a
        // backend deploy) leave the session intact; returning null lets the
        // 401 interceptor reject cleanly without retrying on a stale token.
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        const isAuthRejection = status === 400 || status === 401 || status === 403;
        if (!isAuthRejection) {
            return null;
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.dispatchEvent(new Event('auth-change'));
        if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
        }
        return null;
    }
};

const performTokenRefresh = async (): Promise<string | null> => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        try {
            // CROSS-TAB MUTEX: the backend rotates + blacklists refresh tokens,
            // so two tabs refreshing with the same token race each other — the
            // loser's 401 (blacklisted) arrives before the winner stores the new
            // pair and logs every tab out. The Web Lock serializes refreshes
            // across tabs; refreshTokenPair re-checks storage once inside the
            // lock so waiters reuse the winner's token instead of re-refreshing.
            const locks = (navigator as Navigator & { locks?: LockManager }).locks;
            if (locks) {
                // No timeout/signal on purpose. A closed or crashed tab
                // auto-releases its lock (locks die with the browsing
                // context), and the lock is only held for one refresh
                // round-trip — network responses resolve even in throttled
                // background tabs. A signal-based timeout would let waiters
                // proceed WITHOUT the lock and reintroduce the rotation race.
                return await locks.request('fms-token-refresh', () => refreshTokenPair());
            }
            return await refreshTokenPair();
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
};

// Request interceptor to add JWT token
apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        let token = localStorage.getItem('accessToken');

        if (token) {
            const exp = getAccessTokenExp(token);
            // Proactively refresh if token expires in less than 30 seconds
            if (exp !== null && exp * 1000 - Date.now() < TOKEN_EXPIRY_SKEW_MS) {
                const refreshedToken = await performTokenRefresh();
                if (refreshedToken) {
                    token = refreshedToken;
                }
            }
        }

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        } else {
            // Ensure no stale token from a previous session leaks through defaults
            delete config.headers['Authorization'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Token refresh logic
const refreshAuthLogic = async (failedRequest: any) => {
    const access = await performTokenRefresh();
    if (!access) {
        return Promise.reject();
    }

    // Update the retried request's header with the new token.
    // axios-auth-refresh retries using failedRequest.response.config,
    // so setting it here guarantees the retry carries the correct header
    // even before the request interceptor runs again.
    if (failedRequest.response?.config) {
        failedRequest.response.config.headers['Authorization'] = `Bearer ${access}`;
    }

    return Promise.resolve();
};

// Apply auth refresh interceptor
createAuthRefreshInterceptor(apiClient, refreshAuthLogic, {
    statusCodes: [401],
    pauseInstanceWhileRefreshing: true,
});

// 403 interceptor — dispatch event so PrivateRoute renders AccessDenied in place
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 403) {
            window.dispatchEvent(new CustomEvent('api-forbidden', {
                detail: { pathname: window.location.pathname },
            }));
        }
        return Promise.reject(error);
    }
);

// Legacy Client API - Used by Orders, Holdings, etc. linked to old Client model
export const clientsApi = {
    getClients: (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        client_code?: string;
        email?: string;
        pan_card?: string;
        is_nri?: boolean;
        category?: number;
        group?: number;
        scheme?: number;
        distributor?: number;
    }): Promise<ClientListResponse> => {
        return apiClient.get('/api/clients/', { params }).then(response => response.data);
    },


    getClient: (id: number): Promise<Client> => {
        return apiClient.get(`/api/clients/${id}/`).then(response => response.data);
    },

    createClient: (clientData: ClientFormData): Promise<Client> => {
        return apiClient.post('/api/clients/', clientData).then(response => response.data);
    },

    updateClient: (id: number, clientData: ClientFormData): Promise<Client> => {
        return apiClient.put(`/api/clients/${id}/`, clientData).then(response => response.data);
    },

    deleteClient: (id: number): Promise<void> => {
        return apiClient.delete(`/api/clients/${id}/`);
    },

    activateClient: (id: number): Promise<void> => {
        return apiClient.post(`/api/clients/${id}/activate/`);
    },

    deactivateClient: (id: number): Promise<void> => {
        return apiClient.post(`/api/clients/${id}/deactivate/`);
    },

    getActiveClientsCount: (): Promise<{ count: number }> => {
        return apiClient.get('/api/clients/active_count/').then(response => response.data);
    },
};

export const clientApprovalAPI = {
    getApprovals: (params?: {
        page?: number;
        page_size?: number;
        approval_status?: string;
    }): Promise<ClientApprovalListResponse> => {
        return apiClient.get('/api/client-approvals/', { params }).then(response => response.data);
    },

    getApproval: (id: number): Promise<ClientApproval> => {
        return apiClient.get(`/api/client-approvals/${id}/`).then(response => response.data);
    },

    approveClient: (id: number, data?: { checker_comments?: string }): Promise<{ status: string; message: string }> => {
        return apiClient.post(`/api/client-approvals/${id}/approve/`, data).then(response => response.data);
    },

    rejectClient: (id: number, data: { rejection_reason: string; checker_comments?: string }): Promise<{ status: string; message: string }> => {
        return apiClient.post(`/api/client-approvals/${id}/reject/`, data).then(response => response.data);
    },

    getPendingCount: (): Promise<{ count: number }> => {
        return apiClient.get('/api/client-approvals/count/').then(response => response.data);
    },
};

export const fieldConfigApi = {
    getByClientType: async (clientType: 'PMS' | 'AIF', config?: any): Promise<FieldConfig[]> => {
        const response = await apiClient.get(`/api/field-configs/by_client_type/?client_type=${clientType}`, config);
        return response.data;
    },

    getAvailableFields: async (): Promise<Array<{ value: string, label: string }>> => {
        const response = await apiClient.get('/api/field-configs/available_fields/');
        return response.data;
    },

    bulkUpdate: async (clientType: 'PMS' | 'AIF', fieldConfigs: Array<{ field_name: string, is_required: boolean }>) => {
        const response = await apiClient.post('/api/field-configs/bulk_update/', {
            client_type: clientType,
            field_configs: fieldConfigs
        });
        return response.data;
    }
};

// ClientMaster API - Unified PMS & AIF Client Management (New System)
export const clientMasterApi = {
    getClients: (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        client_code?: string;
        email?: string;
        pan_card?: string;
        is_nri?: boolean;
        category?: number;
        group?: number;
        client_class?: number;
        scheme?: number;
        distributor?: number;
        client_type?: 'PMS' | 'AIF';
        is_active?: boolean;
        stop_purchase?: boolean;
        redemption_only?: boolean;
    }): Promise<ClientListResponse> => {
        return apiClient.get('/api/client-master/', { params }).then(response => response.data);
    },

    getClient: (id: number, config?: any): Promise<Client> => {
        return apiClient.get(`/api/client-master/${id}/`, config).then(response => response.data);
    },

    createClient: (clientData: ClientFormData): Promise<Client> => {
        return apiClient.post('/api/client-master/', clientData).then(response => response.data);
    },

    updateClient: (id: number, clientData: ExtendedClientFormData): Promise<Client> => {
        return apiClient.put(`/api/client-master/${id}/`, clientData).then(response => response.data);
    },

    patchClient: (id: number, clientData: Partial<ClientFormData>): Promise<Client> => {
        return apiClient.patch(`/api/client-master/${id}/`, clientData).then(response => response.data);
    },

    deleteClient: (id: number): Promise<void> => {
        return apiClient.delete(`/api/client-master/${id}/`);
    },

    activateClient: (id: number, data: { change_reason: string }): Promise<void> => {
        return apiClient.post(`/api/client-master/${id}/activate/`, data);
    },

    deactivateClient: (id: number, data: { change_reason: string }): Promise<void> => {
        return apiClient.post(`/api/client-master/${id}/deactivate/`, data);
    },

    // Zoho CRM sync — read_client_master is sufficient; nothing in FMS is mutated.
    // 409 (lead not converted) and 502 (Zoho rejected) carry a result body, so they
    // resolve; only a transport failure or an unexpected status rejects.
    syncClientToZoho: (id: number): Promise<ZohoClientSyncResult> => {
        return apiClient.post(`/api/client-master/${id}/sync-to-zoho/`, undefined, {
            validateStatus: status => status === 200 || status === 409 || status === 502,
        }).then(response => response.data);
    },

    syncAllClientsToZoho: (): Promise<ZohoSyncTotals> => {
        return apiClient.post('/api/client-master/sync-all-to-zoho/').then(response => response.data);
    },

    // Fee Components
    getFeeComponents: (id: number, config?: any): Promise<any[]> => {
        return apiClient.get(`/api/client-master/${id}/fee-components/`, config).then(response => response.data);
    },

    addFeeComponent: (id: number, feeData: any): Promise<any> => {
        return apiClient.post(`/api/client-master/${id}/fee-components/`, feeData).then(response => response.data);
    },

    updateFeeComponent: (clientId: number, feeData: any): Promise<any> => {
        const { id, ...cleanData } = feeData;
        return apiClient.patch(`/api/client-master/${clientId}/fee-components/${id}/update-fee-component/`, { id, ...cleanData }).then(response => response.data);
    },

    deleteFeeComponent: (clientId: number, feeId: number): Promise<void> => {
        return apiClient.delete(`/api/client-master/${clientId}/fee-components/${feeId}/delete-fee-component/`);
    },

    // Contributions
    getContributions: (id: number, config?: any): Promise<any[]> => {
        return apiClient.get(`/api/client-master/${id}/contributions/`, config).then(response => response.data);
    },

    addContribution: (id: number, contributionData: any): Promise<any> => {
        return apiClient.post(`/api/client-master/${id}/add_contribution/`, contributionData).then(response => response.data);
    },

    updateContribution: (clientId: number, contribId: number, contributionData: any): Promise<any> => {
        return apiClient.patch(`/api/client-master/${clientId}/update_contribution/`, { id: contribId, ...contributionData }).then(response => response.data);
    },

    deleteContribution: (clientId: number, contribId: number): Promise<void> => {
        return apiClient.delete(`/api/client-master/${clientId}/delete_contribution/`, { data: { id: contribId } });
    },

    getFeeStructureMetadata: (): Promise<any> => {
        return apiClient.get('/api/client-master/fee-structure-metadata/').then(response => response.data);
    },

    // Fee Notes
    getFeeNotes: (clientId: number, config?: { signal?: AbortSignal }): Promise<ClientFeeNote[]> => {
        return apiClient.get(`/api/client-master/${clientId}/fee-notes/`, config).then(response => response.data.results ?? response.data);
    },

    addFeeNote: (clientId: number, note: string): Promise<ClientFeeNote> => {
        return apiClient.post(`/api/client-master/${clientId}/fee-notes/`, { note }).then(response => response.data);
    },

    updateFeeNote: (clientId: number, noteId: number, note: string): Promise<ClientFeeNote> => {
        return apiClient.patch(`/api/client-master/${clientId}/fee-notes/${noteId}/update-fee-note/`, { note }).then(response => response.data);
    },

    deleteFeeNote: (clientId: number, noteId: number): Promise<void> => {
        return apiClient.delete(`/api/client-master/${clientId}/fee-notes/${noteId}/delete-fee-note/`).then(() => undefined);
    },

    exportClients: (params?: {
        search?: string;
        client_code?: string;
        email?: string;
        pan_card?: string;
        is_nri?: boolean;
        category?: number;
        group?: number;
        client_class?: number;
        scheme?: number;
        distributor?: number;
        client_type?: 'PMS' | 'AIF';
        is_active?: boolean;
        stop_purchase?: boolean;
        redemption_only?: boolean;
        format?: 'xlsx';
    }): Promise<Blob> => {

        const {
            format = 'xlsx',   // default format
            ...filters
        } = params || {};

        // Filter out empty/null/undefined values
        const cleanParams = Object.fromEntries(
            Object.entries(filters).filter(([_, v]) =>
                v !== '' && v !== null && v !== undefined
            )
        );

        return apiClient.get(
            `/api/client-master/export_clients/`,   // 🔥 correct path
            {
                params: cleanParams,   // only filters here
                responseType: 'blob'
            }
        ).then(response => response.data);
    },

    getClientSeriesOptions: (clientId: number): Promise<ClientSeriesOption[]> => {
        return apiClient.get(`/api/client-master/${clientId}/series-options/`).then(response => response.data);
    },


};

export const strategiesApi = {
    getStrategies: (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        firm_type?: number;
        is_active?: boolean;
    }, signal?: AbortSignal): Promise<StrategyListResponse> => {
        return apiClient.get('/api/strategies/', { params, signal }).then(response => response.data);
    },

    getStrategy: (id: number): Promise<Strategy> => {
        return apiClient.get(`/api/strategies/${id}/`).then(response => response.data);
    },

    createStrategy: (strategyData: StrategyFormData): Promise<Strategy> => {
        return apiClient.post('/api/strategies/', strategyData).then(response => response.data);
    },

    updateStrategy: (id: number, strategyData: StrategyFormData): Promise<Strategy> => {
        return apiClient.put(`/api/strategies/${id}/`, strategyData).then(response => response.data);
    },

    deleteStrategy: (id: number): Promise<void> => {
        return apiClient.delete(`/api/strategies/${id}/`);
    },

    activateStrategy: (id: number): Promise<void> => {
        return apiClient.post(`/api/strategies/${id}/activate/`);
    },

    deactivateStrategy: (id: number): Promise<void> => {
        return apiClient.post(`/api/strategies/${id}/deactivate/`);
    },

    getActiveStrategiesCount: (): Promise<{ count: number }> => {
        return apiClient.get('/api/strategies/active_count/').then(response => response.data);
    },

    getFirmTypes: (): Promise<FirmTypesResponse> => {
        return apiClient.get('/api/strategies/firm_types/').then(response => response.data);
    },
};

export const schemesApi = {
    getSchemes: (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        is_active?: boolean;
        investment_strategy?: number;
    }): Promise<SchemeListResponse> => {
        return apiClient.get('/api/schemes/', { params }).then(response => response.data);
    },

    getScheme: (id: number): Promise<Scheme> => {
        return apiClient.get(`/api/schemes/${id}/`).then(response => response.data);
    },

    createScheme: (schemeData: SchemeFormData): Promise<Scheme> => {
        return apiClient.post('/api/schemes/', schemeData).then(response => response.data);
    },

    updateScheme: (id: number, schemeData: SchemeFormData): Promise<Scheme> => {
        return apiClient.put(`/api/schemes/${id}/`, schemeData).then(response => response.data);
    },

    deleteScheme: (id: number): Promise<void> => {
        return apiClient.delete(`/api/schemes/${id}/`);
    },
};

export const companiesApi = {
    getCompanies: (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        symbol?: string;
        isin?: string;
        bse_code?: string;
        nse_code?: string;
        category?: number;
        sector?: number;
        asset_class?: number;
        country?: number;
        restriction_for_nri?: boolean;
        is_active?: boolean;
    }, config?: any): Promise<CompanyListResponse> => {
        return apiClient.get('/api/companies/', { params, ...config }).then(response => {
            if (Array.isArray(response.data)) {
                return {
                    count: response.data.length,
                    next: null,
                    previous: null,
                    results: response.data
                };
            }
            return response.data;
        });
    },

    getCompany: (id: number): Promise<Company> => {
        return apiClient.get(`/api/companies/${id}/`).then(response => response.data);
    },

    // Lightweight {id, name, symbol} list for select inputs — avoids the heavy
    // multi-field list payload when every active company is needed at once.
    getCompanyOptions: (config?: { signal?: AbortSignal }): Promise<Array<{ id: number; name: string; symbol: string; country_code: string | null }>> => {
        return apiClient.get('/api/companies/options/', config).then(response => response.data);
    },

    createCompany: (companyData: CompanyFormData): Promise<Company> => {
        return apiClient.post('/api/companies/', companyData).then(response => response.data);
    },

    updateCompany: (id: number, companyData: CompanyFormData): Promise<Company> => {
        return apiClient.put(`/api/companies/${id}/`, companyData).then(response => response.data);
    },

    // Writes named fields only. The CIO note is edited from the order ticket, where the
    // rest of the company is not loaded and a PUT would blank it.
    patchCompany: (id: number, fields: Partial<Company>): Promise<Company> => {
        return apiClient.patch(`/api/companies/${id}/`, fields).then(response => response.data);
    },

    deleteCompany: (id: number): Promise<void> => {
        return apiClient.delete(`/api/companies/${id}/`);
    },

    activateCompany: (id: number): Promise<void> => {
        return apiClient.post(`/api/companies/${id}/activate/`);
    },

    deactivateCompany: (id: number): Promise<void> => {
        return apiClient.post(`/api/companies/${id}/deactivate/`);
    },

    getActiveCompaniesCount: (): Promise<{ count: number }> => {
        return apiClient.get('/api/companies/active_count/').then(response => response.data);
    },

    getCompanyVolumeStats: (id: number): Promise<CompanyVolumeStatsData> => {
        return apiClient.get(`/api/companies/${id}/volume-stats/`).then(response => response.data);
    },

    getCategories: (config?: any): Promise<CompanyCategory[]> => {

        return apiClient.get('/api/company-categories/', config).then(response => response.data.results || response.data);
    },

    getSectors: (config?: any): Promise<Sector[]> => {
        return apiClient.get('/api/company-sectors/', config).then(response => response.data.results || response.data);
    },

    getAssetClasses: (config?: any): Promise<AssetClass[]> => {
        return apiClient.get('/api/asset-classes/', config).then(response => response.data.results || response.data);
    },

};

export const performanceApi = {
    getClientPerformance: (params: {
        client_master_id: string;
        date: string;
    }): Promise<any> => {
        return apiClient.get('/api/clientwise-performance/', { params }).then(response => response.data);
    },
};

export const supportingDataApi = {
    getCountries: (config?: any): Promise<any[]> => {
        return apiClient.get('/api/countries/', config).then(response => response.data.results || response.data);
    },
    getSchemes: (config?: any): Promise<Scheme[]> => {
        return apiClient.get('/api/schemes/', config).then(response => response.data.results || response.data);
    },

    getInvestmentStrategies: (config?: any): Promise<any[]> => {
        return apiClient
            .get('/api/investment-strategies/', config)
            .then((response) => response.data.results || response.data);
    },

    getClientCategories: (config?: any): Promise<ClientCategory[]> => {
        return apiClient.get('/api/categories/', config).then(response => response.data.results || response.data);
    },

    getClientGroups: (config?: any): Promise<ClientGroup[]> => {
        return apiClient.get('/api/groups/', config).then(response => response.data.results || response.data);
    },

    getClientClasses: (config?: any): Promise<ClientClass[]> => {
        return apiClient.get('/api/client-classes/', config).then(response => response.data.results || response.data);
    },

    getDistributors: (config?: any): Promise<Distributor[]> => {
        return apiClient.get('/api/distributors/', config).then(response => response.data.results || response.data);
    },

    getCompanies: (config?: any): Promise<Company[]> => {
        return apiClient.get('/api/companies/', config).then(response => response.data.results || response.data);
    },

    getCompanyCategories: (config?: any): Promise<CompanyCategory[]> => {
        return apiClient.get('/api/company-categories/', config).then(response => response.data.results || response.data);
    },

    getCompanySectors: (config?: any): Promise<Sector[]> => {
        return apiClient.get('/api/company-sectors/', config).then(response => response.data.results || response.data);
    },

    getAssetClasses: (config?: any): Promise<AssetClass[]> => {
        return apiClient.get('/api/asset-classes/', config).then(response => response.data.results || response.data);
    },

    getOrderEligibleCompanies: (config?: any): Promise<Company[]> => {
        return apiClient.get('/api/companies/order-eligible/', config).then(response => response.data);
    },

    getEtfEligibleCompanies: async (search?: string, page = 1): Promise<{ results: Company[]; hasMore: boolean }> => {
        const response = await apiClient.get('/api/companies/', {
            params: { asset_class__class_name: 'Cash and Equivalent', is_active: true, page_size: 20, page, ...(search ? { search } : {}) }
        });
        const results = response.data.results || [];
        if (page === 1 && !search && !results.length) {
            throw new Error("No ETF-eligible companies found. The 'Cash and Equivalent' asset class may be missing or inactive.");
        }
        return { results, hasMore: !!response.data.next };
    },
};

export const holdingsApi = {
    getHoldings: (params: {
        date: string;
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        client_master?: number;
        company?: number;
        currency?: number;
        client_master__scheme?: number;
        client_master__category?: number;
        client_master__group?: number;
        client_master__is_nri?: boolean;
    }): Promise<HoldingsListResponse> => {
        return apiClient.get('/api/holdings/', { params }).then(response => response.data);
    },

    getHoldingsByClient: (params: {
        date: string;
        client_master_id: number;
    }): Promise<HoldingsByClientResponse> => {
        return apiClient.get('/api/holdings/by_client/', { params }).then(response => response.data);
    },

    getHoldingsByCompany: (params: {
        date: string;
        company_id: number;
    }): Promise<HoldingsByCompanyResponse> => {
        return apiClient.get('/api/holdings/by_company/', { params }).then(response => response.data);
    },

    getHoldingsSummary: (params: {
        date: string;
        group_by?: 'client' | 'company';
    }): Promise<any[]> => {
        return apiClient.get('/api/holdings/summary/', { params }).then(response => response.data);
    },

    getLatestDates: async (params = {}) => {
        const response = await apiClient.get('/api/holdings/latest-date/', {
            params,
        });
        return response.data;
    },
    getHoldingsByScheme: (params: {
        date: string;
        scheme_ids: string;
        asset_class_ids?: string;
        exclude_inherited?: string;
    }): Promise<SchemeWiseHoldingsResponse> => {
        return apiClient.get('/api/holdings/by_scheme/', { params }).then(response => response.data);
    },

    exportAllHoldings: (params: {
        date: string;
        client_master?: number;
        company?: number;
        search?: string;
        ordering?: string;
    }): Promise<HoldingsListResponse> => {
        return apiClient.get('/api/holdings/export-all/', { params }).then(response => response.data);
    },

};

export const orderAllocationsApi = {
    getOrderAllocations: (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        client_master?: number;
        symbol?: number;
        broker_code?: number;
        sell_buy?: number;
        trade_date?: string;
        trade_date__gte?: string;
        trade_date__lte?: string;
    }): Promise<OrderAllocationListResponse> => {
        return apiClient.get('/api/order-allocations/', { params }).then(response => response.data);
    },

    exportAllOrderAllocations: (params?: {
        search?: string;
        ordering?: string;
        client_master?: number;
        sell_buy?: number;
        trade_date__gte?: string;
        trade_date__lte?: string;
    }): Promise<OrderAllocationListResponse> => {
        return apiClient.get('/api/order-allocations/export-all/', { params }).then(response => response.data);
    },
};

export const contractNotesApi = {
    getContractNotes: (params: {
        start_date?: string;
        end_date?: string;
        trade_date?: string;
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        party_code?: number;
        company?: number;
        sell_buy?: number;
        party_code__scheme?: number;
        party_code__category?: number;
        party_code__group?: number;
    }): Promise<ContractNoteListResponse> => {
        return apiClient.get('/api/contract-notes/', { params }).then(response => response.data);
    },

    exportAllContractNotes: (params: {
        start_date?: string;
        end_date?: string;
        ordering?: string;
        company?: number;
        sell_buy?: number;
        party_code__scheme?: number;
    }): Promise<ContractNoteListResponse> => {
        return apiClient.get('/api/contract-notes/export-all/', { params }).then(response => response.data);
    },

    getContractNote: (id: number): Promise<ContractNote> => {
        return apiClient.get(`/api/contract-notes/${id}/`).then(response => response.data);
    },

    getContractNotesByClient: (params: {
        client_master_id: number;
        start_date?: string;
        end_date?: string;
    }): Promise<ContractNotesByClientResponse> => {
        return apiClient.get('/api/contract-notes/by_client/', { params }).then(response => response.data);
    },

    getContractNotesByCompany: (params: {
        company_id: number;
        start_date?: string;
        end_date?: string;
    }): Promise<ContractNotesByCompanyResponse> => {
        return apiClient.get('/api/contract-notes/by_company/', { params }).then(response => response.data);
    },

    getContractNotesSummary: (params: {
        start_date?: string;
        end_date?: string;
        group_by?: 'client' | 'company';
    }): Promise<{
        summary: any[];
        date_range: {
            start_date: string | null;
            end_date: string | null;
        };
    }> => {
        return apiClient.get('/api/contract-notes/summary/', { params }).then(response => response.data);
    },

    uploadContractNotes: (formData: FormData): Promise<ContractFileUploadResponse> => {
        return apiClient.post('/api/orders/contract-notes-upload/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(response => response.data);
    },
};

export const contractFilesApi = {
    getContractFiles: (params?: any): Promise<ContractFileResponse> => {
        return apiClient.get('/api/orders/contract-files/', { params }).then(response => response.data);
    },
    getContractFilesGrouped: (params?: {
        page?: number;
        page_size?: number;
        contract_note_date?: string;
        type?: number;
    }): Promise<{
        count: number;
        next: string | null;
        previous: string | null;
        results: {
            contract_note_date: string;
            file_count: number;
            file_names: string[];
        }[];
    }> => {
        return apiClient.get('/api/orders/contract-files/grouped_by_date/', { params }).then(response => response.data);
    },
    getContractFile: (id: number): Promise<ContractFile> => {
        return apiClient.get(`/api/orders/contract-files/${id}/`).then(response => response.data);
    },
    getContractFileNotes: (id: number): Promise<{
        file_id: number;
        file_name: string;
        count: number;
        notes: ContractNote[];
    }> => {
        return apiClient.get(`/api/orders/contract-files/${id}/notes/`).then(response => response.data);
    },

    deleteContractFile: (id: number): Promise<void> => {
        return apiClient.delete(`/api/orders/contract-files/${id}/`).then(response => response.data);
    },

    deleteAllContractFiles: (params?: { date?: string }): Promise<{ deleted_count: number }> => {
        return apiClient.delete('/api/orders/contract-files/delete_all/', { params }).then(response => response.data);
    },
};

export const orderAllocationApi = {
    // GET /api/orders/allocation/status/?trade_date=YYYY-MM-DD
    // Returns custodians derived from contract notes for that date + their allocation counts
    getAllocationStatus: (date: string): Promise<CustodianAllocationStatus[]> => {
        return apiClient.get('/api/orders/allocation/status/', { params: { trade_date: date } }).then(r => r.data);
    },

    // GET /api/orders/allocation/?trade_date=YYYY-MM-DD[&custodian_id=X]
    getAllocationsByDate: (date: string, custodianId?: number): Promise<OrderAllocation[]> => {
        return apiClient.get('/api/orders/allocation/', {
            params: { trade_date: date, ...(custodianId ? { custodian_id: custodianId } : {}) },
        }).then(r => r.data);
    },

    // POST /api/orders/allocation/<custodian_id>/allocate/
    triggerAllocation: (custodianId: number, date: string, orderCalcType?: number): Promise<{ message: string; errors?: string[] }> => {
        return apiClient.post(`/api/orders/allocation/${custodianId}/allocate/`, {
            trade_date: date,
            ...(orderCalcType != null ? { order_calc_type: orderCalcType } : {})
        }).then(r => r.data);
    },

    // GET /api/orders/allocation/<custodian_id>/download/?trade_date=YYYY-MM-DD
    downloadAllocation: (custodianId: number, date: string, orderCalcType?: number): Promise<Blob> => {
        return apiClient.get(`/api/orders/allocation/${custodianId}/download/`, {
            params: {
                trade_date: date,
                ...(orderCalcType != null ? { order_calc_type: orderCalcType } : {})
            },
            responseType: 'blob',
        }).then(r => r.data);
    },

    // DELETE /api/orders/allocation/<custodian_id>/?trade_date=YYYY-MM-DD
    deleteAllocation: (custodianId: number, date: string, orderCalcType?: number): Promise<{ deleted_count: number }> => {
        return apiClient.delete(`/api/orders/allocation/${custodianId}/`, {
            params: {
                trade_date: date,
                ...(orderCalcType != null ? { order_calc_type: orderCalcType } : {}),
            },
        }).then(r => r.data);
    },

    // POST /api/orders/allocation/send-files/
    sendAllocationEmail: (custodianId: number, date: string, recipients: string[], orderCalcType?: number): Promise<{ message: string; files: string[] }> => {
        return apiClient.post('/api/orders/allocation/send-files/', {
            custodian_id: custodianId,
            trade_date: date,
            recipients,
            ...(orderCalcType != null ? { order_calc_type: orderCalcType } : {})
        }).then(r => r.data);
    },

    // POST /api/orders/allocation/global/fetch-contract-notes/
    // Rebuilds a global scheme's contract notes from the live IBKR session — the global
    // counterpart of uploading a contract-note file. Only covers the current session, and
    // is safe to call repeatedly since each call restates the whole day.
    fetchGlobalContractNotes: (schemeCode: string): Promise<{ status: string; result: GlobalContractNoteFetchResult }> => {
        return apiClient.post('/api/orders/allocation/global/fetch-contract-notes/', {
            scheme_code: schemeCode,
        }).then(r => r.data);
    },

};

export const custodyHoldingsApi = {
    getCustodyHoldings: (params: {
        date: string;
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        client_master?: number;
        company?: number;
        client_master__scheme?: number;
        client_master__category?: number;
        client_master__group?: number;
        client_master__is_nri?: boolean;
    }): Promise<CustodyHoldingsListResponse> => {
        return apiClient.get('/api/custody-holdings/', { params }).then(response => response.data);
    },

    exportAllCustodyHoldings: (params: {
        date: string;
        search?: string;
        ordering?: string;
        client_master?: number;
        company?: number;
        client_master__scheme?: number;
        client_master__category?: number;
        client_master__group?: number;
        client_master__is_nri?: boolean;
    }): Promise<CustodyHoldingsExportResponse> => {
        return apiClient.get('/api/custody-holdings/export-all/', { params }).then(response => response.data);
    },

    getCustodyHolding: (id: number): Promise<CustodyHoldings> => {
        return apiClient.get(`/api/custody-holdings/${id}/`).then(response => response.data);
    },

    getCustodyHoldingsByClient: (params: {
        date: string;
        client_master_id: number;
    }): Promise<CustodyHoldingsByClientResponse> => {
        return apiClient.get('/api/custody-holdings/by_client/', { params }).then(response => response.data);
    },

    getCustodyHoldingsByCompany: (params: {
        date: string;
        company_id: number;
    }): Promise<CustodyHoldingsByCompanyResponse> => {
        return apiClient.get('/api/custody-holdings/by_company/', { params }).then(response => response.data);
    },

    getCustodyHoldingsSummary: (params: {
        date: string;
        group_by?: 'client' | 'company';
    }): Promise<any[]> => {
        return apiClient.get('/api/custody-holdings/summary/', { params }).then(response => response.data);
    },


    getUnsaleablePositions: (params: {
        date: string;
        client_master?: number;
    }): Promise<CustodyHoldings[]> => {
        return apiClient.get('/api/custody-holdings/unsaleable_positions/', { params }).then(response => response.data);
    },
};

export const brokersApi = {
    getBrokersByScheme: (schemeId: number, clientId?: number): Promise<SchemeWiseBroker[]> => {
        let url = `/api/scheme-wise-brokers/by_scheme/?scheme_id=${schemeId}`;
        if (clientId) {
            url += `&client_master_id=${clientId}`;
        }

        return apiClient.get(url).then(response => {
            // Handle paginated response format
            if (response.data.results) {
                return response.data.results;
            }
            // Fallback to direct array if API changes
            return response.data;
        });
    },

    bulkAssignBroker: (orderType: 'buy' | 'sell', orderIds: number[], brokerId: number | null): Promise<any> => {
        return apiClient.patch(`/api/orders/${orderType}/calculated/assign-broker/`, {
            order_ids: orderIds,
            broker_id: brokerId,
        }).then(response => response.data);
    },

    bulkAssignEtfBroker: (side: 'buy' | 'sell', orderIds: number[], brokerId: number | null): Promise<any> => {
        return apiClient.patch(`/api/orders/etf_${side}/calculated/assign-broker/`, {
            order_ids: orderIds,
            broker_id: brokerId,
        }).then(response => response.data);
    },
};

// Order submits carry no order_date: the backend stamps it at creation (auto_now_add),
// so a form left open past midnight or a mis-set client clock cannot date an order.
export const ordersApi = {
    submitSellOrder: (data: {
        scheme_id: number;
        company_id?: number;
        h1: number;
        h2: number;
        condition_1: number;
        condition_2: number;
        min_order_val: number;
        sell_method: number;
        order_type: number;
        median_volume_pct: number;
        apply_volume_cap: boolean;
        is_only_long_term: boolean;
        long_term_months?: number;
        is_redemption: boolean;
        is_full_redemption: boolean;
        client_ids: number[];
    }): Promise<{
        order_id?: number;
        count?: number;
        scheme_id?: number;
        company_id?: number;
        order_count?: number;
        total_quantity?: number;
        total_amount?: string;
        orders?: Array<{
            client_code: string;
            client_name: string;
            client_id: number;
            quantity: number;
            amount: string;
            order_time: string;
        }>;
    }> => {
        return apiClient.post('/api/orders/sell/submit/', data).then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    getPartialRedemptionHoldings: (params: {
        client_master_id: number;
        is_inherited?: boolean;
    }): Promise<{
        /** Spendable cash, already net of block amount, pending buys and T+1 receivables. */
        instant_cash: number;
        /** Buy-order cash reserve; null when it cannot be computed. */
        cash_reserve: number | null;
        /** Cash a redemption can pay out before selling stock (0 when the reserve is unknown). */
        available_cash: number;
        holdings: Array<{
            company_id: number;
            company_symbol: string;
            company_name: string;
            is_inherited: boolean;
            quantity: number;
            holding_aum_pct: number;
            current_value: number;
            unit_price: number;
        }>;
    }> => {
        return apiClient.get('/api/holdings/order-holdings/', { params }).then(response => response.data);
    },

    submitBuyOrder: (data: {
        scheme_id: number;
        company_id: number;
        cl1: number;
        cl2: number;
        increase_to_1: number;
        increment_max_1: number;
        increase_to_2: number;
        increment_max_2: number;
        increase_by_1: number;
        increase_by_2: number;
        limit_price: number;
        /** ISO currency limit_price is quoted in; omitted means already base currency. */
        currency_code?: string;
        max_holding: number;

        min_order_val: number;
        buy_method: number;
        cash_utilization_buffer_pct: number;
        median_volume_pct: number;
        apply_volume_cap: boolean;
        /** Client-wise order on a scheme that allows it; omitted or empty means the whole scheme. */
        client_ids?: number[];
    }): Promise<{
        order_id: number;
        scheme_id: number;
        company_id: number;
        status: string;
        message: string;
    }> => {
        // Convert cash_utilization_buffer_pct to integer before sending
        const requestData = {
            ...data,
            cash_utilization_buffer_pct: data.cash_utilization_buffer_pct
        };
        return apiClient.post('/api/orders/buy/submit/', requestData).then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    getProcessedBuyInstructions: (params?: {
        scheme_id?: number;
        company_id?: number;
    }): Promise<{
        count: number;
        orders: Array<{
            id: number;
            order_date: string;
            scheme: { id: number; name: string; region?: number | null };
            company: { id: number; symbol: string; company_name: string };
            limit_price: string;
            max_holding: string;
            min_order_val: string;
            order_status_display: string;
            created_at: string;
            median_volume_pct: number;
            apply_volume_cap: boolean;
        }>;
    }> => {
        return apiClient.get('/api/orders/buy/processed-instruction/', { params }).then(response => response.data);
    },

    getInProgressBuyOrders: (params?: {
        scheme_id?: number;
        company_id?: number;
    }): Promise<{
        count: number;
        orders: Array<{
            id: number;
            order_date: string;
            scheme: { id: number; name: string; region?: number | null };
            company: { id: number; symbol: string; company_name: string };
            limit_price: string;
            max_holding: string;
            min_order_val: string;
            created_at: string;
            median_volume_pct: number;
            apply_volume_cap: boolean;
        }>;
    }> => {
        return apiClient.get('/api/orders/buy/inprogress/', { params }).then(response => response.data);
    },

    getProcessedSellInstructions: (params?: {
        scheme_id?: number;
        company_id?: number;
    }): Promise<{
        count: number;
        orders: Array<{
            id: number;
            order_date: string;
            scheme: { id: number; name: string; region?: number | null };
            company: { id: number; symbol: string; company_name: string };
            min_order_val: number;
            order_status_display: string;
            created_at: string;
            median_volume_pct: number;
            apply_volume_cap: boolean;
        }>;
    }> => {
        return apiClient.get('/api/orders/sell/processed-instruction/', { params }).then(response => response.data);
    },

    getInProgressSellOrders: (params?: {
        scheme_id?: number;
        company_id?: number;
    }): Promise<{
        count: number;
        orders: Array<{
            id: number;
            order_date: string;
            scheme: { id: number; name: string; region?: number | null };
            company: { id: number; symbol: string; company_name: string };
            min_order_val: number;
            created_at: string;
            median_volume_pct: number;
            apply_volume_cap: boolean;
        }>;
    }> => {
        return apiClient.get('/api/orders/sell/inprogress/', { params }).then(response => response.data);
    },

    processBuyOrders: (data: {
        order_ids: number[];
        block_trade?: boolean;
    }): Promise<{
        processed_count: number;
        processed_orders: Array<{
            order_id: number;
            scheme_id: number;
            company_id: number;
            order_count: number;
            total_quantity: number;
            total_amount: string;
        }>;
        errors: Array<{
            order_id: number;
            error: string;
        }>;
    }> => {
        return apiClient.post('/api/orders/buy/process/', data).then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    processSellOrders: (data: {
        order_ids: number[];
        block_trade?: boolean;
    }): Promise<{
        processed_count: number;
        processed_orders: Array<{
            order_id: number;
            scheme_id: number;
            company_id: number;
            order_count: number;
            total_quantity: number;
            total_amount: string;
        }>;
        errors: Array<{
            order_id: number;
            error: string;
        }>;
    }> => {
        return apiClient.post('/api/orders/sell/process/', data).then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    deleteBuyOrders: (data: {
        order_ids: number[];
    }): Promise<{
        deleted_count: number;
        error?: string;
    }> => {
        return apiClient.delete('api/orders/buy/delete/', { data })
            .then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    deleteSellOrders: (data: {
        order_ids: number[];
    }): Promise<{
        deleted_count: number;
        error?: string;
    }> => {
        return apiClient.delete('api/orders/sell/delete/', { data })
            .then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    getCalculatedBuyOrders: (params?: {
        order_id?: number;
        client_id?: number;
        scheme_id?: number;
        company_id?: number;
        start_date?: string;
        end_date?: string;
        order_status?: number;
        page?: number;
        page_size?: number;
        aggregate_non_nri?: boolean;
    }): Promise<{
        count: number;
        total_quantity: number;
        total_amount: string;
        currency: string | null;
        calculated_orders?: Array<{
            id: number;
            order_id: number;
            order_date: string;
            order_status: number;
            order_status_display: string;
            client: {
                id: number;
                code: string;
                name: string;
                is_nri: boolean;
            };
            scheme: {
                id: number;
                code: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            quantity: number;
            amount: string;
            remaining_qty: number;
            order_time: string;
            broker: {
                id: number;
                code: string;
            } | null;
            created_at: string;
            updated_at: string;
        }>;
        aggregated_non_nri?: Array<{
            scheme: {
                id: number;
                code: string;
                currency: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            order_id: number;
            total_quantity: number;
            total_amount: string;
            order_count: number;
            order_ids: number[];
            broker: {
                id: number;
                code: string;
            } | null;
        }>;
        nri_orders?: Array<{
            id: number;
            order_id: number;
            order_date: string;
            order_status: number;
            order_status_display: string;
            client: {
                id: number;
                code: string;
                name: string;
                is_nri: boolean;
            };
            scheme: {
                id: number;
                code: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            quantity: number;
            amount: string;
            remaining_qty: number;
            order_time: string;
            broker: {
                id: number;
                code: string;
            } | null;
            created_at: string;
            updated_at: string;
        }>;
    }> => {
        return apiClient.get('/api/orders/buy/calculated/', { params }).then(response => response.data);
    },
    getCalculatedSellOrders: (params?: {
        order_id?: number;
        client_id?: number;
        scheme_id?: number;
        company_id?: number;
        start_date?: string;
        end_date?: string;
        order_status?: number;
        page?: number;
        page_size?: number;
        aggregate_non_nri?: boolean;
    }): Promise<{
        count: number;
        total_quantity: number;
        total_amount: string;
        currency: string | null;
        calculated_orders?: Array<{
            id: number;
            order_id: number;
            order_date: string;
            order_status: number;
            order_status_display: string;
            client: {
                id: number;
                code: string;
                name: string;
                is_nri: boolean;
            };
            scheme: {
                id: number;
                code: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            quantity: number;
            amount: string;
            remaining_qty: number;
            order_time: string;
            broker: {
                id: number;
                code: string;
            } | null;
            created_at: string;
            updated_at: string;
        }>;
        aggregated_non_nri?: Array<{
            scheme: {
                id: number;
                code: string;
                currency: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            order_id: number;
            total_quantity: number;
            total_amount: string;
            order_count: number;
            order_ids: number[];
            broker: {
                id: number;
                code: string;
            } | null;
        }>;
        nri_orders?: Array<{
            id: number;
            order_id: number;
            order_date: string;
            order_status: number;
            order_status_display: string;
            client: {
                id: number;
                code: string;
                name: string;
                is_nri: boolean;
            };
            scheme: {
                id: number;
                code: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            quantity: number;
            amount: string;
            remaining_qty: number;
            order_time: string;
            broker: {
                id: number;
                code: string;
            } | null;
            created_at: string;
            updated_at: string;
        }>;
    }> => {
        return apiClient.get('/api/orders/sell/calculated/', { params }).then(response => response.data);
    },

    deleteCalculatedBuyOrder: (orderId: number): Promise<{
        message: string;
        deleted_order_id: number;
        client_code: string;
        parent_order_id: number;
    }> => {
        return apiClient.delete(`/api/orders/buy/calculated/${orderId}/`).then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    submitCalculatedOrders: (data: {
        order_ids: number[];
        email_recipients?: string[];
    }): Promise<{
        submission_id: string;
        orders_submitted: number;
        files_generated: {
            [key: string]: string;
        };
        email_sent: boolean;
        email_recipients: string[];
        submission_date: string;
    }> => {
        return apiClient.post('/api/orders/buy/calculated/submit/', data).then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    submitCalculatedSellOrders: (data: {
        order_ids: number[];
        email_recipients?: string[];
    }): Promise<{
        submission_id: string;
        orders_submitted: number;
        files_generated: {
            [key: string]: string;
        };
        email_sent: boolean;
        email_recipients: string[];
        submission_date: string;
    }> => {
        return apiClient.post('/api/orders/sell/calculated/submit/', data).then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    // ETF order APIs
    // cash_env is always sent. A percentage order sends cash_env 4 (CASH_ENV_PERCENTAGE)
    // together with aum_pct (buy this % of each client's AUM); the backend rejects aum_pct
    // under any other cash_env.
    submitEtfBuyOrder: (data: {
        scheme_id: number;
        company_id: number;
        cash_env: number;
        aum_pct?: number;
        limit_price: number;
        /** Client-wise order on a scheme that allows it; omitted or empty means the whole scheme. */
        client_ids?: number[];
    }): Promise<{ order_id: number; status: string; message: string }> => {
        return apiClient.post('/api/orders/etf_buy/submit/', data).then(response => response.data)
            .catch(error => { throw error.response?.data ?? error; });
    },

    // company_id is omitted for a global scheme: the instrument is resolved per client
    // from holdings, and no cash-environment band applies, so cash_env is optional there
    // (the form still sends T+0 so the stored order records it). Such a
    // submit answers with { count } instead of a single order_id. A percentage order sends
    // cash_env 4 (CASH_ENV_PERCENTAGE) together with holding_pct (sell this % of the ETF
    // holding), and always a company_id.
    submitEtfSellOrder: (data: {
        scheme_id: number;
        company_id?: number;
        cash_env?: number;
        holding_pct?: number;
        /** Client-wise order on a scheme that allows it; omitted or empty means the whole scheme. */
        client_ids?: number[];
    }): Promise<{ order_id?: number; count?: number; status: string; message: string }> => {
        return apiClient.post('/api/orders/etf_sell/submit/', data).then(response => response.data)
            .catch(error => { throw error.response?.data ?? error; });
    },

    getInProgressEtfBuyOrders: (): Promise<{ count: number; orders: any[] }> => {
        return apiClient.get('/api/orders/etf_buy/inprogress/').then(response => response.data);
    },

    getInProgressEtfSellOrders: (): Promise<{ count: number; orders: any[] }> => {
        return apiClient.get('/api/orders/etf_sell/inprogress/').then(response => response.data);
    },

    processEtfBuyOrders: (data: { order_ids: number[] }): Promise<any> => {
        return apiClient.post('/api/orders/etf_buy/process/', data).then(response => response.data)
            .catch(error => { throw error.response?.data ?? error; });
    },

    processEtfSellOrders: (data: { order_ids: number[] }): Promise<any> => {
        return apiClient.post('/api/orders/etf_sell/process/', data).then(response => response.data)
            .catch(error => { throw error.response?.data ?? error; });
    },

    getCalculatedEtfBuyOrders: (params?: {
        page?: number;
        page_size?: number;
        order_status?: number;
        scheme_id?: number;
        company_id?: number;
        broker?: number;
    }): Promise<{ count: number; calculated_orders: any[]; aggregated_non_nri?: any[]; nri_orders?: any[] }> => {
        return apiClient.get('/api/orders/etf_buy/calculated/', { params }).then(response => response.data);
    },

    getCalculatedEtfSellOrders: (params?: {
        page?: number;
        page_size?: number;
        order_status?: number;
        scheme_id?: number;
        company_id?: number;
        broker?: number;
    }): Promise<{ count: number; calculated_orders: any[]; aggregated_non_nri?: any[]; nri_orders?: any[] }> => {
        return apiClient.get('/api/orders/etf_sell/calculated/', { params }).then(response => response.data);
    },

    submitCalculatedEtfBuyOrders: (data: { order_ids: number[]; email_recipients?: string[] }): Promise<any> => {
        return apiClient.post('/api/orders/etf_buy/calculated/submit/', data).then(response => response.data)
            .catch(error => { throw error.response?.data ?? error; });
    },

    submitCalculatedEtfSellOrders: (data: { order_ids: number[]; email_recipients?: string[] }): Promise<any> => {
        return apiClient.post('/api/orders/etf_sell/calculated/submit/', data).then(response => response.data)
            .catch(error => { throw error.response?.data ?? error; });
    },

    // Callers reload the orders after a save, so the echoed updated_orders is not returned.
    bulkUpdateEtfCalculatedOrderQuantities: (side: 'buy' | 'sell', updates: QuantityUpdate[]): Promise<void> => {
        return apiClient.patch(`/api/orders/etf_${side}/calculated/quantities/`, { updates }).then(() => undefined)
            .catch(error => { throw error.response?.data ?? error; });
    },

    getProcessedEtfBuyInstructions: (): Promise<{ count: number; orders: any[] }> => {
        return apiClient.get('/api/orders/etf_buy/processed-instruction/').then(response => response.data);
    },

    getProcessedEtfSellInstructions: (): Promise<{ count: number; orders: any[] }> => {
        return apiClient.get('/api/orders/etf_sell/processed-instruction/').then(response => response.data);
    },

    getCompletedEtfBuyOrders: (params?: { page_size?: number; aggregate_non_nri?: boolean }): Promise<any> => {
        return apiClient.get('/api/orders/etf_buy/completed/', { params }).then(response => response.data);
    },

    getCompletedEtfSellOrders: (params?: { page_size?: number; aggregate_non_nri?: boolean }): Promise<any> => {
        return apiClient.get('/api/orders/etf_sell/completed/', { params }).then(response => response.data);
    },

    deleteEtfBuyOrders: (data: { order_ids: number[] }): Promise<{ deleted_count: number; error?: string }> => {
        return apiClient.delete('/api/orders/etf_buy/delete/', { data })
            .then(response => response.data)
            .catch(error => { throw error.response?.data ?? error; });
    },

    deleteEtfSellOrders: (data: { order_ids: number[] }): Promise<{ deleted_count: number; error?: string }> => {
        return apiClient.delete('/api/orders/etf_sell/delete/', { data })
            .then(response => response.data)
            .catch(error => { throw error.response?.data ?? error; });
    },

    getClientCash: (data: {
        client_ids: number[];
    }): Promise<{
        holding_date: string;
        cash_data: Array<{
            client_id: number;
            client_code: string;
            client_name: string;
            instant_cash: string;
            aum: string;
            cash_plus_liquid: string;
            utilized_cash: string;
            cash_plus_liquid_pct: string;
            t_1_receivables: string;
        }>;
    }> => {
        return apiClient.post('/api/orders/client-cash/', data).then(response => response.data)
            .catch(error => {
                if (error.response && error.response.data) {
                    throw error.response.data;
                }
                throw error;
            });
    },

    getSchemeCash: (): Promise<{
        holding_date: string;
        instant_cash: Array<{
            scheme_id: number;
            scheme_name: string;
            aggr_pct: number;
            buckets: {
                '0-1': number;
                '1-3': number;
                '3-7': number;
                '7-12': number;
                '12-20': number;
                '20-40': number;
                '40-70': number;
                '70-100': number;
            };
        }>;
        cash_plus_liquid: Array<{
            scheme_id: number;
            scheme_name: string;
            aggr_pct: number;
            buckets: {
                '0-1': number;
                '1-3': number;
                '3-7': number;
                '7-12': number;
                '12-20': number;
                '20-40': number;
                '40-70': number;
                '70-100': number;
            };
        }>;
    }> => {
        return apiClient.get('/api/orders/scheme-cash/').then(response => response.data);
    },

    getPortfolioPerformanceXIRR: (params?: { date?: string; keys?: string; start_date?: string; end_date?: string; region?: number }): Promise<XirrPortfolioPerformanceResponse> => {
        return apiClient.get('/api/portfolio-performance-xirr/', { params }).then(response => response.data);
    },

    getPortfolioPerformanceTWRR: (params?: { date?: string; keys?: string; start_date?: string; end_date?: string; region?: number }): Promise<TwrrPortfolioPerformanceResponse> => {
        return apiClient.get('/api/portfolio-performance-twrr/', { params }).then(response => response.data);
    },

    getBenchmarkPerformance: (params?: { date?: string; keys?: string; start_date?: string; end_date?: string }): Promise<BenchmarkPerformanceResponse> => {
        return apiClient.get('/api/benchmark-performance/', { params }).then(response => response.data);
    },

    getStockWisePerformance: (params: { scheme_codes: string; exclude_inherited: string }): Promise<{
        status: string;
        holdings_date: string;
        total_aum: number;
        data: {
            base_currency: string;
            company_id: number;
            name: string;
            symbol: string;
            unit_cost: number;
            unit_price: number;
            total_quantity: number;
            current_price: number;
            market_value_cr: number | null;
            percentage_of_aum: number | null;
            total_change_cr: number | null;
            total_change_percent: number | null;
            today_change_cr: number | null;
            today_change_percent: number | null;
        }[];
    }> => {
        return apiClient.get('/api/stock-wise-performance/', { params }).then(response => response.data);
    },

    getCompletedBuyOrders: (params?: {
        scheme_id?: number;
        company_id?: number;
        start_date?: string;
        end_date?: string;
        page?: number;
        page_size?: number;
    }): Promise<{
        count: number;
        total_quantity: number;
        total_amount: string;
        currency: string | null;
        calculated_orders?: Array<{
            id: number;
            order_id: number;
            order_date: string;
            order_status: number;
            order_status_display: string;
            client: {
                id: number;
                code: string;
                name: string;
                is_nri?: boolean;
            };
            scheme: {
                id: number;
                code: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            quantity: number;
            amount: string;
            remaining_qty?: number;
            order_time?: string;
            broker: {
                id: number;
                code: string;
            } | null;
            created_at: string;
            updated_at: string;
        }>;
        aggregated_non_nri?: Array<{
            scheme: {
                id: number;
                code: string;
                currency: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            order_id: number;
            total_quantity: number;
            total_amount: string;
            order_count: number;
            order_ids: number[];
            broker: {
                id: number;
                code: string;
            } | null;
        }>;
        nri_orders?: Array<{
            id: number;
            order_id: number;
            order_date: string;
            order_status: number;
            order_status_display: string;
            client: {
                id: number;
                code: string;
                name: string;
                is_nri?: boolean;
            };
            scheme: {
                id: number;
                code: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            quantity: number;
            amount: string;
            remaining_qty?: number;
            order_time?: string;
            broker: {
                id: number;
                code: string;
            } | null;
            created_at: string;
            updated_at: string;
        }>;
    }> => {
        return apiClient.get('/api/orders/buy/completed/', { params }).then(response => response.data);
    },
    getCompletedSellOrders: (params?: {
        scheme_id?: number;
        company_id?: number;
        start_date?: string;
        end_date?: string;
        page?: number;
        page_size?: number;
    }): Promise<{
        count: number;
        total_quantity: number;
        total_amount: string;
        currency: string | null;
        calculated_orders?: Array<{
            id: number;
            order_id: number;
            order_date: string;
            order_status: number;
            order_status_display: string;
            client: {
                id: number;
                code: string;
                name: string;
                is_nri?: boolean;
            };
            scheme: {
                id: number;
                code: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            quantity: number;
            amount: string;
            remaining_qty?: number;
            order_time?: string;
            broker: {
                id: number;
                code: string;
            } | null;
            created_at: string;
            updated_at: string;
        }>;
        aggregated_non_nri?: Array<{
            scheme: {
                id: number;
                code: string;
                currency: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            order_id: number;
            total_quantity: number;
            total_amount: string;
            order_count: number;
            order_ids: number[];
            broker: {
                id: number;
                code: string;
            } | null;
        }>;
        nri_orders?: Array<{
            id: number;
            order_id: number;
            order_date: string;
            order_status: number;
            order_status_display: string;
            client: {
                id: number;
                code: string;
                name: string;
                is_nri?: boolean;
            };
            scheme: {
                id: number;
                code: string;
            };
            company: {
                id: number;
                symbol: string;
                name: string;
            };
            quantity: number;
            amount: string;
            remaining_qty?: number;
            order_time?: string;
            broker: {
                id: number;
                code: string;
            } | null;
            created_at: string;
            updated_at: string;
        }>;
    }> => {
        return apiClient.get('/api/orders/sell/completed/', { params }).then(response => response.data);
    },
};

export const orderRecommendationApi = {
    getOrders: async (params?: { strategy?: string[]; start_date?: string; categories_list?: string }): Promise<import("@/components/order_recommendation/types").PendingOrder[]> => {
        return apiClient
            .get("/api/order-recommendations/", { params })
            .then(res => res.data);
    },

    createOrder: async (orderData: {
        order_type: number;
        company: number;
        strategy_group?: "LHF" | "CORE";
        limit_price?: string;
        cio_target_pct?: string;
        comment?: string;
        schemes?: number[];
    }): Promise<import("@/components/order_recommendation/types").PendingOrder> => {
        return apiClient
            .post("/api/order-recommendations/", orderData)
            .then(res => res.data);
    },

    updateOrder: async (
        orderId: number,
        updates: {
            limit_price?: string;
            target_holding_pct?: string;
            cio_target_pct?: string;
            cio_decision?: string | null;
            comment?: string;
            is_completed?: boolean;
        }
    ): Promise<import("@/components/order_recommendation/types").PendingOrder> => {
        return apiClient
            .patch(`/api/order-recommendations/${orderId}/`, updates)
            .then(res => res.data);
    },

    deleteOrder: async (orderId: number): Promise<void> => {
        return apiClient
            .delete(`/api/order-recommendations/${orderId}/`)
            .then(() => undefined);
    },

    bulkSaveOrders: async (payload: {
        updates: ({ id: number } & Partial<{
            limit_price: string | null; target_holding_pct: string; cio_target_pct: string;
            cio_decision: string | null; comment: string; execution_type: "limit" | "market";
            execution_method: "vwap" | "pct_volume" | null; volume_pct: string;
            priority: "high" | "medium" | "low";
            // Completing a row rides in the same payload, so one save is one request.
            is_completed: boolean;
        }>)[];
        creates: ({ company: number; strategy_group: "LHF" | "CORE"; order_type: 1 | 2 } & Partial<{
            limit_price: string | null; target_holding_pct: string; cio_target_pct: string;
            cio_decision: string | null; comment: string; execution_type: "limit" | "market";
            execution_method: "vwap" | "pct_volume" | null; volume_pct: string;
            priority: "high" | "medium" | "low";
        }>)[];
    }): Promise<void> => {
        return apiClient
            .post("/api/order-recommendations/bulk-save/", payload)
            .then(() => undefined);
    },

    continuePrevDayOrders: async (
        scope: "domestic" | "global" = "domestic"
    ): Promise<{ continued: number }> => {
        return apiClient
            .post("/api/order-recommendations/continue-prev-day/", null, { params: { scope } })
            .then(res => res.data);
    },

    // Signs the day's Domestic book off: messages the trade desk, changes no order.
    // `notified: false` means nothing was on Continue when the request landed.
    markDomesticComplete: async (): Promise<{ notified: boolean }> => {
        return apiClient
            .post("/api/order-recommendations/mark-domestic-complete/")
            .then(res => res.data);
    },

    // Fetches Long-Term and Short-Term active AIF lot quantities, keyed by company id
    fetchAifLotsAgeSplit: async (
        companyIds: number[],
        config?: { signal?: AbortSignal }
    ): Promise<Record<number, {
        lt_qty: number | null;
        st_qty: number | null;
        oldest_st_date: string | null;
        oldest_st_age_days: number | null;
    }>> => {
        return apiClient
            .post("/api/order-recommendations/aif-lots-age-split/", { company_ids: companyIds }, { signal: config?.signal })
            .then(res => res.data);
    },

    // Minimal active-tracker summary for one company — powers the Quick Decision
    // form's status panel. No list-view enrichment, so it responds instantly.
    getCompanyPending: async (
        companyId: number,
        config?: { signal?: AbortSignal }
    ): Promise<Array<{
        id: number;
        order_type: 1 | 2;
        strategy_group: "CORE" | "LHF" | null;
        execution_type: "limit" | "market";
        execution_method: "vwap" | "pct_volume" | null;
        volume_pct: string | null;
        limit_price: string | null;
        cio_decision: string | null;
        cio_target_pct: string | null;
        comment: string | null;
        priority: "high" | "medium" | "low";
        latest_order_date: string | null;
        target_holding_pct: string | null;
    }>> => {
        return apiClient
            .get("/api/order-recommendations/company-pending/", {
                params: { company: companyId },
                ...config,
            })
            .then(res => res.data);
    },

    // Upsert a CIO decision: updates the matching active tracker per strategy
    // group, or creates one when none exists. "BOTH" covers CORE + LHF.
    // If a group's tracker runs in the opposite direction, the backend answers
    // 409 {conflicts} unless convert_existing is true (user gave permission).
    quickDecision: async (payload: {
        company: number;
        order_type: 1 | 2;
        strategy_group: "CORE" | "LHF" | "BOTH";
        execution_type?: "limit" | "market";
        execution_method?: "vwap" | "pct_volume";
        volume_pct?: string | null;
        limit_price?: string | null;
        cio_target_pct: string;
        comment?: string;
        priority?: "high" | "medium" | "low";
        convert_existing?: boolean;
    }): Promise<{
        results: Array<{
            strategy_group: "CORE" | "LHF";
            action: "created" | "updated" | "converted";
            id: number;
        }>;
    }> => {
        return apiClient
            .post("/api/order-recommendations/quick-decision/", payload)
            .then(res => res.data);
    },
};


export const tradeSuggestionsApi = {
    getTradeSuggestions: (params?: {
        categories_list?: string;
        isin_list?: string;
        countries_list?: string;
        strategy_codes?: string;
        start_date?: string;
        end_date?: string;
        suggestions_flag?: boolean;
    }, signal?: AbortSignal): Promise<TradeSuggestionsResponse> => {
        return apiClient.get('api/orders/trade-suggestions/', { params, signal }).then(response => response.data);
    },
};

export const notificationsApi = {

    // List notifications for a user
    listNotifications: (params: {
        user_id: number;
        type?: string;
        cursor?: string | null;
        days?: number;
        page_size?: number;
        start_date?: string;
        end_date?: string;
    }, config?: any): Promise<any> => {
        return apiClient.get('/api/notifications/list_notifications/', { params, ...(config || {}) }).then(res => res.data);
    },

    // Mark a notification as read
    markAsRead: (notificationId: number, user_id: number): Promise<{
        success: boolean;
        message?: string;
        read_ts?: string;
    }> => {
        return apiClient.post(`/api/notifications/${notificationId}/mark_read/`, { user_id }).then(res => res.data);
    },

    // Mark a notification as completed
    markAsCompleted: (notificationId: number, user_id: number): Promise<{
        success: boolean;
        status: string;
        completed_ts: string;
    }> => {
        return apiClient.post(`/api/notifications/${notificationId}/mark_completed/`, { user_id }).then(res => res.data);
    },

    // Mark all notifications as read for a user
    markAllAsRead: (user_id: number): Promise<{
        success: boolean;
        updated_count: number;
    }> => {
        return apiClient.post('/api/notifications/mark_all_read/', { user_id }).then(res => res.data);
    },
};


export interface PeakMarginScheme {
    scheme_id: number;
    scheme_code: string | null;
    scheme_name: string | null;
    available_cash: number;
    peak_margin: number;
    buy_amount: number;
    sell_amount: number;
    stored_peak_margin: number | null;
    // What was stored on the last working day — the source for "copy from last working day".
    previous_peak_margin: number | null;
}

export interface PeakMarginResponse {
    current_date: string | null;
    margin_percentage: number;
    previous_margin_date: string | null;
    schemes: PeakMarginScheme[];
}

export const marginCalculationAPI = {
    getMarginCalculation: (params: {
        margin_percentage: number;
    }): Promise<{
        order_date: string;
        margin_percentage: number;
        non_nri_order_data: Array<{
            client_master__scheme: number;
            client_master__scheme__code?: string;
            total_amount: number;
            margin_amount: number;
        }>;
    }> => {
        return apiClient.get('/api/orders/margin-calculation/', { params }).then(res => res.data);
    },
    getPeakMargin: (): Promise<PeakMarginResponse> => {
        return apiClient.get('/api/orders/peak-margin/').then(res => res.data);
    },
    savePeakMargin: (payload: {
        schemes: Array<{ scheme_id: number; amount: number }>;
    }): Promise<{ margin_date: string; saved: number }> => {
        return apiClient.post('/api/orders/peak-margin/save/', payload).then(res => res.data);
    }
};

export const portfolioTransactionAPI = {
    getPortfolioTransactions: async (params: {
        client_ids?: number[];
        scheme_ids?: number[];
        from_date?: string;
        to_date?: string;
    }): Promise<any> => {
        return apiClient
            .get("/api/portfolio-transactions/", { params })
            .then((res) => res.data);
    },
};

export const distributorContributionAPI = {
    getDistributorContribution: async (params: {
        scheme_ids?: number[];
        from_date?: string;
        to_date?: string;
    }): Promise<any> => {
        return apiClient
            .get("/api/distributor-contribution/", { params })
            .then((res) => res.data);
    },
}

export const cashEnvironmentAPI = {
    getCashEnvironment: async (params?: {
        cash_env?: 0 | 1 | "0" | "1" | "t0";
        view_type?: "figure" | "percent";
        // Scheme code to restrict to, "0" for every scheme, or "exclude" for the
        // default (everything but SCPL and SIML).
        scheme_filter?: string;
    }): Promise<{
        holding_date: string;
        to_be_invested: number;
        to_be_liquidated: number;
        scheme_warnings: string[];
        data: Array<{
            client_id: number;
            client_code: string;
            client_name: string;
            scheme: string;
            aum: number;
            bank_balance: number;
            liquid_amt: number;
            clearing_cash: number;
            bank_balance_liquid_pct: number | null;
            sell_amount: number;
            amt_redeem: number;
            net_liquid: number;
            net_cash: number;
        }>;
    }> => {
        return apiClient
            .get("/api/orders/cash-environment/", { params })
            .then((res) => res.data);
    },

    // GET /api/orders/liquidity-file/download/
    downloadLiquidityFile: (): Promise<{ blob: Blob; filename: string }> => {
        return apiClient.get('/api/orders/liquidity-file/download/', {
            responseType: 'blob',
        }).then(r => {
            const today = new Date();
            const dd = String(today.getDate()).padStart(2, '0');
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const yyyy = today.getFullYear();
            const filename = `0051_SAMEEKSHA FUTURE MARGIN out_DUMMY ENTRY for Trade ${dd}.${mm}.${yyyy}.xlsx`;
            return { blob: r.data, filename };
        });
    },
};

export const sellValidationAPI = {
    getSellValidation: async (params?: {
        holding_date?: string;
    }): Promise<{
        holding_date: string;
        count: number;
        data: Array<{
            client_code: string;
            client_name: string;
            scheme: string;
            symbol: string;
            holding_quantity: number;
            sell_quantity: number;
            remaining_quantity: number;
            sell_amount: number;
        }>;
    }> => {
        return apiClient
            .get("/api/orders/sell/validation/", { params })
            .then((res) => res.data);
    },
};

export const executionAnalyticsApi = {
    getAnalytics: (params: ExecutionAnalyticsRequest): Promise<any> => {
        return apiClient.get('/api/analytics/', { params });
    }
};

export const brokerComparisonApi = {
    getBrokerComparison: (params: BrokerageComparisonRequest): Promise<any> => {
        return apiClient.get('/api/analytics/broker-comparison/', { params });
    }
};

export const brokerWiseBrokerageApi = {
    getBrokerWiseBrokerage: (params: BrokerWiseBrokerageRequest & { ordering?: string }): Promise<any> => {
        return apiClient.get('/api/analytics/broker-wise-brokerage/', { params });
    }
};

export const portfolioLiqRiskAPI = {
    getPortfolioLiquidityRisk: (params?: PortfolioLiquidityRiskRequest): Promise<PortfolioLiquidityRiskResponse> => {
        return apiClient
            .get('/api/risk-calculation/portfolio-liquidity-risk', { params })
            .then((res) => res.data);
    },
};

export const marketDataStalenessAPI = {
    getMarketDataStaleness: (): Promise<any> => {
        return apiClient.get("/api/market-data-staleness/").then((res) => res.data);
    },
    /**
     * `ltp` comes back in the currency it is quoted in, named by `currency_code` — the
     * canonical major-unit code, e.g. "GBP" for a GBX-listed contract.
     */
    getLivePrice: (companyId: number): Promise<LivePrice> => {
        return apiClient.get(`/api/live-price/${companyId}/`).then((res) => {
            const data = res.data;
            // Detail endpoint returns {company_id: {...}} — unwrap to a flat object
            const entry = data[companyId] ?? data[String(companyId)] ?? {};
            return {
                company_id: companyId,
                ltp: entry.ltp ?? null,
                currency_code: entry.currency_code ?? null,
                exchange_code: entry.exchange_code ?? null,
                ltp_timestamp: entry.ltp_timestamp ?? null,
            };
        });
    },
};

export const commonApi = {
    // `days` is the number of working days to go back from today (default 1 = T-1).
    // The global performance section passes days=2 for T-2, since global data lags a day.
    getLastWorkingDay: async (params?: { days?: number }): Promise<{ last_working_day: string }> => {
        const response = await apiClient.get('/api/last-working-day/', { params });
        return response.data;
    },

    getLatestUsdInrRate: async (): Promise<UsdInrRate> => {
        const response = await apiClient.get('/api/usd-inr-reference-rate/latest/');
        return response.data;
    },

};

export const exposureRiskAPI = {
    getExposureRisk: (params: ExposureRiskRequest): Promise<ExposureRiskResponse> => {
        return apiClient.get('/api/risk-calculation/overall-exposure-risk/', { params }).then(response => response.data);
    },
};

export const sectorWeightAPI = {
    getSectorWeightComparison: (params: SectorWeightRequest): Promise<SectorWeightResponse> => {
        return apiClient.get('/api/sector-weight-comparison/', { params }).then(response => response.data);
    },
    getIndices: (): Promise<any[]> => {
        return apiClient.get('/api/indices/').then(response => response.data);
    },
};

export const portfolioPEApi = {
    getPortfolioPE: (date: string, excludedCompanyIds?: number[]): Promise<PortfolioPEApiResponse> => {
        const params: { excluded_company_ids?: string } = {};
        if (excludedCompanyIds && excludedCompanyIds.length > 0) {
            params.excluded_company_ids = excludedCompanyIds.join(',');
        }
        return apiClient.get(`/api/analytics/portfolio-pe/${date}/`, { params }).then(response => response.data);
    },
};

export const brokerMasterApi = {
    getBrokers: (params?: any) => apiClient.get('/api/brokers/', { params }).then(res => res.data.results || res.data),
    list: (params?: any) => apiClient.get('/api/brokers/', { params }).then(res => res.data),
    create: (data: any) => apiClient.post('/api/brokers/', data).then(res => res.data),
    update: (id: number, data: any) => apiClient.put(`/api/brokers/${id}/`, data).then(res => res.data),
    partialUpdate: (id: number, data: any) => apiClient.patch(`/api/brokers/${id}/`, data).then(res => res.data),
    delete: (id: number) => apiClient.delete(`/api/brokers/${id}/`),
};

export const schemeWiseBrokersApi = {
    list: (params?: any) => apiClient.get('/api/scheme-wise-brokers/', { params }).then(res => res.data),
    create: (data: any) => apiClient.post('/api/scheme-wise-brokers/', data).then(res => res.data),
    update: (id: number, data: any) => apiClient.put(`/api/scheme-wise-brokers/${id}/`, data).then(res => res.data),
    delete: (id: number) => apiClient.delete(`/api/scheme-wise-brokers/${id}/`),
};

export const vendorSymbolMappingApi = {
    getMappings: (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        vendor_code?: string;
        company?: number;
    }): Promise<VendorSymbolMappingListResponse> => {
        return apiClient.get('/api/vendor-symbol-mappings/', { params }).then(response => response.data);
    },

    getMapping: (id: number): Promise<VendorSymbolMapping> => {
        return apiClient.get(`/api/vendor-symbol-mappings/${id}/`).then(response => response.data);
    },

    createMapping: (data: VendorSymbolMappingFormData): Promise<VendorSymbolMapping> => {
        return apiClient.post('/api/vendor-symbol-mappings/', data).then(response => response.data);
    },

    updateMapping: (id: number, data: VendorSymbolMappingFormData): Promise<VendorSymbolMapping> => {
        return apiClient.put(`/api/vendor-symbol-mappings/${id}/`, data).then(response => response.data);
    },

    deleteMapping: (id: number): Promise<void> => {
        return apiClient.delete(`/api/vendor-symbol-mappings/${id}/`);
    },
};

export const faProcessorApi = {
    getSources: (): Promise<FASource[]> => {
        return apiClient.get('/api/fa-processor/sources/').then(response => response.data);
    },

    processHoldings: (data: {
        source: string;
        portfolio_file?: File | null;
        cash_file?: File | null;
        provisions_file?: File | null;
        skip_email?: boolean;
    }): Promise<any> => {
        const formData = new FormData();
        formData.append('source', data.source);
        if (data.portfolio_file) {
            formData.append('portfolio_file', data.portfolio_file);
        }
        if (data.cash_file) {
            formData.append('cash_file', data.cash_file);
        }
        if (data.provisions_file) {
            formData.append('provisions_file', data.provisions_file);
        }
        if (data.skip_email !== undefined) {
            formData.append('skip_email', data.skip_email.toString());
        }

        return apiClient.post('/api/fa-processor/process/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(response => response.data);
    },
};

export interface SchemeCustodyDate {
    id: number;
    name: string;
    code: string | null;
    /** Custodian account the scheme's latest statement arrived under, e.g. "EC0000287"; null for API-fed schemes (IBKR issues no file). */
    custody_code: string | null;
    /** Whether the account is fetched from the broker, and so issues no statement to download. */
    is_api_based: boolean;
    latest_holding_date: string | null;
}

export const custodyFilesApi = {
    /** Latest custody holding date per scheme; schemes with no custody data come back with a null date. */
    getSchemeWiseLatestDates: (): Promise<{ scheme_wise_latest_custody_dates: SchemeCustodyDate[] }> => {
        return apiClient.get('/api/custody-holdings/scheme-wise-latest-date/').then(response => response.data);
    },

    /** The accounts a statement can be uploaded or fetched for. */
    getAccounts: (): Promise<{ results?: CustodyAccount[] } | CustodyAccount[]> => {
        return apiClient.get('/api/custody-accounts/').then(response => response.data);
    },

    getFiles: (params?: {
        page?: number;
        page_size?: number;
        custody_holding_date?: string;
        custody_code?: string;
        ordering?: string;
    }): Promise<CustodyHoldingFileListResponse> => {
        return apiClient.get('/api/custody-holding-files/', { params }).then(response => response.data);
    },

    /** Uploads and ingests in one request — the response carries what was written. */
    uploadFile: (file: File, accountId: number, date: string): Promise<CustodyIngestResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('custody_account', String(accountId));
        formData.append('holding_date', date);

        return apiClient.post('/api/custody-holding-files/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(response => response.data);
    },

    /** Pulls holdings from the broker for an account that issues no statement. */
    fetchApiHoldings: (accountId: number, date: string): Promise<{ status: boolean; written: number }> => {
        return apiClient.post('/api/custody-holding-files/fetch-api-holdings/', {
            custody_account: accountId,
            holding_date: date,
        }).then(response => response.data);
    },

    deleteFile: (id: number): Promise<void> => {
        return apiClient.delete(`/api/custody-holding-files/${id}/`);
    },
};

/** Custody accounts as a general master — the same endpoint the upload page reads. */
export const custodyAccountsApi = {
    list: (params?: any) => apiClient.get('/api/custody-accounts/', { params }).then(res => res.data),
    create: (data: any) => apiClient.post('/api/custody-accounts/', data).then(res => res.data),
    update: (id: number, data: any) => apiClient.put(`/api/custody-accounts/${id}/`, data).then(res => res.data),
    delete: (id: number) => apiClient.delete(`/api/custody-accounts/${id}/`),
};

export const bankStatementApi = {
    getBankStatements: (params: {
        statement_date: string;
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        client_master?: number;
        account_number?: string;
    }): Promise<BankStatementListResponse> => {
        return apiClient.get('/api/bank-statements/', { params }).then(response => response.data);
    },

    uploadFile: (files: File[]): Promise<any> => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        return apiClient.post('/api/bank-statements/upload/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(response => response.data);
    },
};

export const mastersApi = {

    // Countries
    getCountries: (params?: any) => apiClient.get('/api/countries/', { params }).then(res => res.data),

    getCountry: (id: number) => apiClient.get(`/api/countries/${id}/`).then(res => res.data),

    createCountry: (data: any) => apiClient.post('/api/countries/', data).then(res => res.data),

    updateCountry: (id: number, data: any) => apiClient.put(`/api/countries/${id}/`, data).then(res => res.data),

    deleteCountry: (id: number) => apiClient.delete(`/api/countries/${id}/`),


    // Categories
    getCategories: (params?: any) => apiClient.get('/api/categories/', { params }).then(res => res.data),

    getCategory: (id: number) => apiClient.get(`/api/categories/${id}/`).then(res => res.data),

    createCategory: (data: any) => apiClient.post('/api/categories/', data).then(res => res.data),

    updateCategory: (id: number, data: any) => apiClient.put(`/api/categories/${id}/`, data).then(res => res.data),

    deleteCategory: (id: number) => apiClient.delete(`/api/categories/${id}/`),

    // Groups
    getGroups: (params?: any) => apiClient.get('/api/groups/', { params }).then(res => res.data),

    getGroup: (id: number) => apiClient.get(`/api/groups/${id}/`).then(res => res.data),

    createGroup: (data: any) => apiClient.post('/api/groups/', data).then(res => res.data),

    updateGroup: (id: number, data: any) => apiClient.put(`/api/groups/${id}/`, data).then(res => res.data),

    deleteGroup: (id: number) => apiClient.delete(`/api/groups/${id}/`),

    // Distributors
    getDistributors: (params?: any) => apiClient.get('/api/distributors/', { params }).then(res => res.data),

    getDistributor: (id: number) => apiClient.get(`/api/distributors/${id}/`).then(res => res.data),

    createDistributor: (data: any) => apiClient.post('/api/distributors/', data).then(res => res.data),

    updateDistributor: (id: number, data: any) => apiClient.put(`/api/distributors/${id}/`, data).then(res => res.data),

    deleteDistributor: (id: number) => apiClient.delete(`/api/distributors/${id}/`),

    // Client Classes
    getClientClasses: (params?: any) => apiClient.get('/api/client-classes/', { params }).then(res => res.data),

    getClientClass: (id: number) => apiClient.get(`/api/client-classes/${id}/`).then(res => res.data),

    createClientClass: (data: any) => apiClient.post('/api/client-classes/', data).then(res => res.data),

    updateClientClass: (id: number, data: any) => apiClient.put(`/api/client-classes/${id}/`, data).then(res => res.data),

    deleteClientClass: (id: number) => apiClient.delete(`/api/client-classes/${id}/`),

    // Field Configs
    getFieldConfigs: (params?: any) => apiClient.get('/api/field-configs/', { params }).then(res => res.data),

    getFieldConfig: (id: number) => apiClient.get(`/api/field-configs/${id}/`).then(res => res.data),

    createFieldConfig: (data: any) => apiClient.post('/api/field-configs/', data).then(res => res.data),

    updateFieldConfig: (id: number, data: any) => apiClient.put(`/api/field-configs/${id}/`, data).then(res => res.data),

    deleteFieldConfig: (id: number) => apiClient.delete(`/api/field-configs/${id}/`),

};

// ── Kite Connect API ──────────────────────────────────────────────────────────
export const kiteApi = {
    /** Returns the Zerodha Kite login URL to redirect the user to. */
    getLoginUrl: (): Promise<{ login_url: string }> =>
        apiClient.get('/api/kite/login-url/').then(res => res.data),

    /** Exchanges a request_token received from Kite for a stored access_token. */
    submitCallback: (requestToken: string): Promise<{
        message: string;
        access_token: string;
        expires_at: string;
    }> =>
        apiClient.post('/api/kite/callback/', { request_token: requestToken }).then(res => res.data),

    /** Returns whether there is a valid access token for today. */
    getStatus: (): Promise<{ connected: boolean; access_token?: string }> =>
        apiClient.get('/api/kite/status/').then(res => res.data),
};

export const managementFeesApi = {
    getQuarterlyFees: (params: {
        quarter?: string;
        investment_strategy?: number;
        scheme?: number;
        region?: string;
    }): Promise<{
        results: ManagementFeeRow[];
        summary: ManagementFeeSummary;
    }> => apiClient.get('/api/management-fees/', { params }).then(res => res.data),

    getClientDetail: (clientCode: string): Promise<{
        client_info: ManagementFeeClientInfo;
        quarters: ManagementFeeQuarter[];
    }> => apiClient.get(`/api/management-fees/${clientCode}/`).then(res => res.data),
};

export interface ManagementFeeRow {
    client_code: string;
    client_name: string;
    scheme_code: string | null;
    is_active: boolean;
    client_type: string;
    management_fee_pct: string | null;
    nuvama_avg_aum: number | null;
    nuvama_fee: number | null;
    nuvama_fee_gst: number | null;
    holding_avg_aum: number | null;
    holding_fee: number | null;
    holding_fee_gst: number | null;
    cash_plus_liquid: number;
    distributor_share_pct: number | null;
    distributor_name: string | null;
    nuvama_distribution_fee: number | null;
    holding_distribution_fee: number | null;
}

export interface ManagementFeeSummary {
    total_clients: number;
    total_nuvama_fee: number;
    total_nuvama_fee_gst: number;
    total_holding_fee: number;
    total_holding_fee_gst: number;
    cash_date: string | null;
    total_nuvama_distribution_fee: number | null;
    total_holding_distribution_fee: number | null;
}

export interface ManagementFeeClientInfo {
    client_code: string;
    client_name: string;
    scheme_code: string | null;
    strategy_name: string | null;
    management_fee_pct: string | null;
    management_fee_valid_from?: string;
    cash_plus_liquid?: number;
    cash_date?: string;
    distributor_share_pct: number | null;
}

export interface ManagementFeeQuarter {
    quarter: string;
    start_date: string;
    end_date: string;
    nuvama_avg_aum: number | null;
    nuvama_fee: number | null;
    nuvama_fee_gst: number | null;
    holding_avg_aum: number | null;
    holding_fee: number | null;
    holding_fee_gst: number | null;
    daily_nuvama: DailyFeeBreakdown[];
    daily_holding: DailyFeeBreakdown[];
    nuvama_distribution_fee: number | null;
    holding_distribution_fee: number | null;
}

export interface DailyFeeBreakdown {
    date: string;
    aum: number | null;
    daily_fee: number | null;
    daily_fee_gst: number | null;
    mgmt_pct: number | null;
}

export const orderExecutionApi = {
    /** Get execution matrix (Company × Scheme pivot) */
    getMatrix: (params: {
        order_side: 1 | 2;
        execution_status?: number;
        execution_date?: string;
        execution_date__gte?: string;
        execution_date__lte?: string;
    }): Promise<ExecutionMatrixResponse> =>
        apiClient.get('/api/orders/executions/matrix/', { params }).then(res => res.data),

    /** Fetch bulk CMP for multiple companies */
    getBulkCmp: (companyIds: number[]): Promise<Record<number, { ltp: number | null; ltp_timestamp: string | null }>> =>
        apiClient.post('/api/live-price/bulk/', { company_ids: companyIds }).then(res => res.data),

    /** List all order executions */
    list: (params?: Record<string, any>): Promise<any[]> =>
        apiClient.get('/api/orders/executions/', { params }).then(res => res.data),

    /** Create a new order execution */
    create: (data: {
        company: number;
        scheme: number;
        order_side: 1 | 2;
        buy_order?: number | null;
        sell_order?: number | null;
        execution_date: string;
        limit_price: string;
        ordered_qty: number;
        remarks?: string;
    }): Promise<any> =>
        apiClient.post('/api/orders/executions/', data).then(res => res.data),

    /** Update executed_qty and/or limit_price */
    update: (id: number, data: {
        executed_qty?: number;
        limit_price?: string;
    }): Promise<any> =>
        apiClient.patch(`/api/orders/executions/${id}/`, data).then(res => res.data),

    /** Cancel an order execution */
    cancel: (id: number): Promise<any> =>
        apiClient.post(`/api/orders/executions/${id}/cancel/`).then(res => res.data),

    /** Close a partially filled execution (done for day) */
    close: (id: number): Promise<any> =>
        apiClient.post(`/api/orders/executions/${id}/close/`).then(res => res.data),

    /** Get limit price change history */
    getPriceHistory: (id: number): Promise<any[]> =>
        apiClient.get(`/api/orders/executions/${id}/price-history/`).then(res => res.data),

    /** Update total POV % for all executions of a company/side/date */
    updateTotalPov: (data: {
        company_id: number;
        order_side: 1 | 2;
        total_pov_percentage: number;
    }): Promise<{ detail: string; total_pov_percentage: number }> =>
        apiClient.patch('/api/orders/executions/update-total-pov/', data).then(res => res.data),
};

export const orderGenerationFailuresApi = {
    list: (params?: {
        page?: number;
        page_size?: number;
        ordering?: string;
        search?: string;
        client_master?: number;
        company?: number;
        order_type?: number;
        reason_category?: string;
        date_from?: string;
        date_to?: string;
    }): Promise<ClientOrderGenerationFailureInfoListResponse> =>
        apiClient.get('/api/orders/generation-failures/', { params }).then(res => res.data),

    // Unpaginated {value, label} options for the reason filter dropdown.
    getReasonCategories: (): Promise<FailureReasonCategory[]> =>
        apiClient.get('/api/orders/generation-failures/reason-categories/').then(res => res.data),
};

export const usersApi = {
    getUsers: (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        role?: number;
        is_active?: boolean;
    }): Promise<SystemUserListResponse> =>
        apiClient.get('/api/auth/users/', { params }).then(res => res.data),

    getUser: (id: number): Promise<SystemUser> =>
        apiClient.get(`/api/auth/users/${id}/`).then(res => res.data),

    createUser: (data: SystemUserFormData): Promise<SystemUser> =>
        apiClient.post('/api/auth/users/', data).then(res => res.data),

    updateUser: (id: number, data: Partial<SystemUserFormData>): Promise<SystemUser> =>
        apiClient.patch(`/api/auth/users/${id}/`, data).then(res => res.data),

    deleteUser: (id: number): Promise<void> =>
        apiClient.delete(`/api/auth/users/${id}/`),

    toggleActive: (id: number): Promise<SystemUser> =>
        apiClient.patch(`/api/auth/users/${id}/toggle_active/`).then(res => res.data),
};

export const rolesApi = {
    getRoles: (): Promise<Role[]> =>
        apiClient.get('/api/auth/roles/').then(res => res.data),

    getRole: (id: number): Promise<RoleDetail> =>
        apiClient.get(`/api/auth/roles/${id}/`).then(res => res.data),

    createRole: (data: { name: string }): Promise<Role> =>
        apiClient.post('/api/auth/roles/', data).then(res => res.data),

    updateRole: (id: number, data: { name: string }): Promise<Role> =>
        apiClient.put(`/api/auth/roles/${id}/`, data).then(res => res.data),

    deleteRole: (id: number): Promise<void> =>
        apiClient.delete(`/api/auth/roles/${id}/`),

    setRoleScopes: (id: number, scopeIds: number[]): Promise<RoleDetail> =>
        apiClient.put(`/api/auth/roles/${id}/scopes/`, { scope_ids: scopeIds }).then(res => res.data),

    getScopes: (): Promise<Scope[]> =>
        apiClient.get('/api/auth/scopes/').then(res => res.data),
};

export interface QAResponse {
    answer: string;
    sql: string;
    params: (string | number | null)[];
    columns: string[];
    rows: (string | number | null)[][];
    latency_ms: number;
    prompt_tokens: number;
    output_tokens: number;
}

export interface QAHistoryEntry {
    id: number;
    question: string;
    answer: string;
    row_count: number;
    latency_ms: number;
    created_ts: string;
    has_error: boolean;
}

export interface QAHistoryData {
    sql: string;
    params: (string | number | null)[];
    columns: string[];
    rows: (string | number | null)[][];
}

export const qaApi = {
    ask: (question: string): Promise<QAResponse> =>
        apiClient.post('/api/qa/ask/', { question }).then(res => res.data),

    getHistory: (limit = 50): Promise<QAHistoryEntry[]> =>
        apiClient.get('/api/qa/history/', { params: { limit } }).then(res => res.data.results ?? res.data),

    getHistoryData: (id: number): Promise<QAHistoryData> =>
        apiClient.get(`/api/qa/history/${id}/data/`).then(res => res.data),
};

export const settingsApi = {
    getSettings: (): Promise<Setting[]> =>
        apiClient.get('/api/settings/').then(res => res.data.results ?? res.data),
    updateSetting: (id: number, value: string): Promise<Setting> =>
        apiClient.patch(`/api/settings/${id}/`, { value }).then(res => res.data),
};

export const holdingsDateRestrictionApi = {
    getRestriction: (): Promise<HoldingsDateRestriction> =>
        apiClient.get('/api/holdings-date-restriction/').then(res => res.data),
};

// Operations Checklist — daily end-of-day validation steps for the operations team.
// All endpoints require the `operations_checklist` scope (401 unauth / 403 missing scope).
export const operationsApi = {
    // Current working day's checklist; the backend builds + runs auto-checks on first access.
    getCurrent: (): Promise<OperationsChecklist> =>
        apiClient.get('/api/operations/checklist/current/').then(res => res.data),

    // Paginated list of past checklists (DRF page envelope).
    list: (params?: { page?: number; page_size?: number; ordering?: string }): Promise<ChecklistListResponse> =>
        apiClient.get('/api/operations/checklist/', { params }).then(res => res.data),

    getById: (id: number): Promise<OperationsChecklist> =>
        apiClient.get(`/api/operations/checklist/${id}/`).then(res => res.data),

    // Re-run every automatic check; manual/exempted decisions are preserved.
    refreshChecklist: (id: number): Promise<OperationsChecklist> =>
        apiClient.post(`/api/operations/checklist/${id}/refresh/`).then(res => res.data),

    // Mark the checklist complete; 400 with `unresolved_steps` if any step is unresolved.
    completeChecklist: (id: number): Promise<OperationsChecklist> =>
        apiClient.post(`/api/operations/checklist/${id}/complete/`).then(res => res.data),

    getStep: (id: number): Promise<OperationStep> =>
        apiClient.get(`/api/operations/steps/${id}/`).then(res => res.data),

    // Re-evaluate a single AUTO step.
    refreshStep: (id: number): Promise<OperationStep> =>
        apiClient.post(`/api/operations/steps/${id}/refresh/`).then(res => res.data),

    // Waive a step with a mandatory reason (3–2000 chars).
    exemptStep: (id: number, reason: string): Promise<OperationStep> =>
        apiClient.post(`/api/operations/steps/${id}/exempt/`, { reason }).then(res => res.data),

    // Confirm a MANUAL step as completed.
    confirmStep: (id: number): Promise<OperationStep> =>
        apiClient.post(`/api/operations/steps/${id}/confirm/`).then(res => res.data),
};

// ── Block Trade deal book ─────────────────────────────────────────────────────
export const blockTradeApi = {
    getBlockTrades: (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ordering?: string;
        status?: BlockTradeStatus;
        party_type?: BlockTradePartyType;
        company?: number;
        broker?: number;
        investment_strategies?: number;
        trade_date?: string;
        trade_date__gte?: string;
        trade_date__lte?: string;
    }): Promise<PaginatedResponse<BlockTrade>> =>
        apiClient.get('/api/block-trades/', { params }).then(res => res.data),

    getBlockTrade: (id: number): Promise<BlockTrade> =>
        apiClient.get(`/api/block-trades/${id}/`).then(res => res.data),

    /** Omitting trade_date/status lets the backend default them to today / Available. */
    createBlockTrade: (data: BlockTradeCreatePayload): Promise<BlockTrade> =>
        apiClient.post('/api/block-trades/', data).then(res => res.data),

    updateBlockTrade: (id: number, data: BlockTradeUpdatePayload): Promise<BlockTrade> =>
        apiClient.patch(`/api/block-trades/${id}/`, data).then(res => res.data),

    /** Status transition to Expired. 400s if the block is already expired. */
    expireBlockTrade: (id: number): Promise<BlockTrade> =>
        apiClient.post(`/api/block-trades/${id}/expire/`).then(res => res.data),

    /** Rebuild the stored desk snapshot against current market/portfolio data.
     *  Overwrites the figures the original decision was taken on. */
    refreshSnapshot: (id: number): Promise<BlockTrade> =>
        apiClient.post(`/api/block-trades/${id}/refresh-snapshot/`).then(res => res.data),

    /** Replace the desk's typed-in inputs and rebuild the snapshot around them.
     *  A PUT: the body is the complete set, so a field left out (or sent as null) is
     *  cleared and its sourced value shows through again. */
    setManualOverrides: (id: number, data: BlockTradeOverridePayload): Promise<BlockTrade> =>
        apiClient.put(`/api/block-trades/${id}/manual-overrides/`, data).then(res => res.data),

    /** Post a block trade to the desk's Google Chat space as a card.
     *  Build the payload with `buildBlockTradeChatCard` — the figures go over already
     *  formatted, the backend only lays them out, and it logs the send against the
     *  operator and the row. */
    sendToChat: (
        id: number,
        data: BlockTradeChatSharePayload,
    ): Promise<BlockTradeChatShareResponse> =>
        apiClient.post(`/api/block-trades/${id}/send-to-chat/`, data).then(res => res.data),

    deleteBlockTrade: (id: number): Promise<void> =>
        apiClient.delete(`/api/block-trades/${id}/`).then(() => undefined),
};

export const contributionAnalysisApi = {
    getContributionAnalysis: (params: {
        from_date: string;
        to_date: string;
        scheme_ids?: string;   // comma-separated Scheme PKs
        client_ids?: string;   // comma-separated ClientMaster PKs
    }): Promise<ContributionAnalysisResponse> =>
        apiClient.get('/api/contribution-analysis/', { params }).then(res => res.data),
};

interface SoaDocumentFilterParams {
    page?: number;
    page_size?: number;
    period_end?: string;
    unmatched?: 'true';
    verification?: 'verified' | 'unverified';
    email_status?: SoaEmailStatus;
    client_active?: 'true' | 'false';
    client_master?: number;
    scheme?: number;
    search?: string;
    fa_source?: string;
    ordering?: string;
}

interface SoaMissingClientParams {
    period_end: string;
    fa_source?: string;
    scheme?: number;
    client_active?: 'true' | 'false';
    page_size?: number;
}

export const soaApi = {
    getDocuments: (params?: SoaDocumentFilterParams): Promise<SoaDocumentListResponse> =>
        apiClient.get('/api/soa-documents/', { params }).then(res => res.data),

    // Clients with no statement for the month — the gap between the client base and
    // what was actually uploaded.
    getMissingClients: (params: SoaMissingClientParams): Promise<SoaMissingClientsResponse> =>
        apiClient.get('/api/soa-documents/missing-clients/', { params }).then(res => res.data),

    downloadMissingClients: (params: SoaMissingClientParams): Promise<Blob> =>
        apiClient.get('/api/soa-documents/missing-clients/download/', { params, responseType: 'blob' })
            .then(res => res.data),

    // Accepts a single PDF or a zip; the backend extracts and matches every file.
    // Any mix of PDFs and zips; the backend reads the repeated "files" key.
    upload: (files: File[], periodEnd: string, zipPassword?: string): Promise<SoaUploadSummary> => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        formData.append('period_end', periodEnd);
        if (zipPassword) formData.append('zip_password', zipPassword);

        return apiClient.post('/api/soa-documents/upload/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then(res => res.data);
    },

    assignClient: (id: number, clientMasterId: number): Promise<SoaAssignResponse> =>
        apiClient.patch(`/api/soa-documents/${id}/assign-client/`, {
            client_master_id: clientMasterId,
        }).then(res => res.data),

    download: (id: number): Promise<Blob> =>
        apiClient.get(`/api/soa-documents/${id}/download/`, { responseType: 'blob' }).then(res => res.data),

    // On-demand, PAN-encrypted copy of one report variant — for internal preview,
    // not a send. Same page-slicing/disclaimer-merge logic as the real dispatch.
    // disclaimerMode is the explicit pick from the download's context-edit dialog;
    // omitted, the backend runs its automatic text/pdf/default cascade.
    downloadReport: (id: number, variant: SoaReportVariant, disclaimerMode?: SoaDisclaimerMode): Promise<Blob> =>
        apiClient.get(`/api/soa-documents/${id}/download-report/`, {
            params: { variant, disclaimer_mode: disclaimerMode },
            responseType: 'blob',
        }).then(res => res.data),

    downloadEmailLog: (id: number): Promise<Blob> =>
        apiClient.get(`/api/soa-documents/${id}/email-log/download/`, { responseType: 'blob' })
            .then(res => res.data),

    // Every send attempt across whatever the current list filters resolve to, not
    // just the current page.
    downloadEmailLogsBulk: (params?: SoaDocumentFilterParams): Promise<Blob> =>
        apiClient.get('/api/soa-documents/email-logs/download/', { params, responseType: 'blob' })
            .then(res => res.data),

    deleteDocument: (id: number): Promise<void> =>
        apiClient.delete(`/api/soa-documents/${id}/`),

    // A disclaimer PDF at a fixed GCS path, and/or custom text (both can be set —
    // which one is used is an explicit per-send/per-download choice), appended to
    // monthly statements.
    getDisclaimerStatus: (): Promise<SoaDisclaimerStatus> =>
        apiClient.get('/api/soa-documents/disclaimer/').then(res => res.data),

    uploadDisclaimer: (file: File): Promise<SoaDisclaimerStatus> => {
        const formData = new FormData();
        formData.append('file', file);
        return apiClient.post('/api/soa-documents/disclaimer/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then(res => res.data);
    },

    // Becomes the new "latest text" everywhere — pre-filled next time the picker
    // or an automatic download cascade reads the disclaimer.
    saveDisclaimerText: (text: string): Promise<SoaDisclaimerStatus> =>
        apiClient.post('/api/soa-documents/disclaimer/text/', { text }).then(res => res.data),

    downloadDisclaimer: (): Promise<Blob> =>
        apiClient.get('/api/soa-documents/disclaimer/preview/', { responseType: 'blob' })
            .then(res => res.data),

    // quarterly picks Full Report vs 2 Pages + Disclaimer for the whole batch.
    // force re-sends documents already marked sent; it never overrides eligibility
    // (unassigned and non-PMS rows are skipped regardless).
    // testRecipient redirects every message to one address — honoured outside
    // production only; the server ignores it in production.
    // disclaimerMode is the sender's explicit Text/PDF toggle choice for this batch.
    sendEmails: (
        ids: number[], quarterly: boolean, force = false, testRecipient?: string,
        disclaimerMode?: SoaDisclaimerMode,
    ): Promise<SoaSendSummary> =>
        apiClient.post('/api/soa-documents/send/', {
            ids,
            quarterly,
            force,
            ...(testRecipient ? { test_recipient: testRecipient } : {}),
            ...(disclaimerMode ? { disclaimer_mode: disclaimerMode } : {}),
        }).then(res => res.data),

    // The send run currently in progress, or null. Polled while one is live so the
    // Send button stays locked and the counts move without a page reload.
    getSendStatus: (): Promise<{ job_run: SoaSendJobState | null }> =>
        apiClient.get('/api/soa-documents/send-status/').then(res => res.data),

    // Reads the sending mailbox for delivery-failure reports and marks the matching
    // statements bounced. Also runs daily on a cron; this is the on-demand path.
    checkEmailStatus: (sinceDays?: number): Promise<SoaBounceScanSummary> =>
        apiClient.post('/api/soa-documents/check-email-status/',
            sinceDays ? { since_days: sinceDays } : {}).then(res => res.data),

    // Custom subject/body if saved, else the built-in default — same template used for every send.
    getEmailTemplate: (): Promise<SoaEmailTemplate> =>
        apiClient.get('/api/soa-documents/email-template/').then(res => res.data),

    updateEmailTemplate: (subject: string, body: string): Promise<SoaEmailTemplate> =>
        apiClient.patch('/api/soa-documents/email-template/', { subject, body }).then(res => res.data),
};

export default apiClient;
